import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from 'react-cookie';
import AdminSharedPresetPanel from "./AdminSharedPresetPanel";
import HoldAdminManualModal from "./HoldAdminManualModal";
import OpenPositionsSnapshotSection from "./OpenPositionsSnapshotSection";
import SymbolSearchPopup from "./SymbolSearchPopup";
import {
  applyDecimalPctInputChange,
  decimalPctToNumber,
  formatDecimalPctInputValue,
  normalizeDecimalPctOnBlur,
} from "./decimalPctInput";
import { bandHoldShowsProfitRoi, isBandHybridHoldMode } from "./bandBulkFieldConfig";

/** 스냅샷 버튼 기준으로 관리자 수동 모달을 띄울 때 고정 위치용 */
const snapshotAnchorRect = (el) => {
  if (typeof el?.getBoundingClientRect !== 'function') return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height };
};

const BAND_ACCESS_STORAGE_KEY = 'bandAccessContext';

const BAND_DEFAULT_HEDGE_HL_BAR_COUNT = 20;
const BAND_DEFAULT_CLOSE_TP_HL_BAR_COUNT = 5;
const BAND_DCA_STEP_COUNT = 10;

const createEmptyBandDcaSteps = () =>
  Array.from({ length: BAND_DCA_STEP_COUNT }, () => ({ usdt: 0, gap_pct: 0 }));

function normalizeBandDcaMode(val) {
  const v = String(val || '').trim().toLowerCase();
  if (v === 'extreme' || v === 'kinpoint') return v;
  return 'disable';
}

function isBandHedgeGridExhaustMode(mode) {
  return String(mode || '').trim().toLowerCase() === 'grid_exhaust';
}

function resolveBandDcaModeForHedge(hedgeMode, dcaMode) {
  if (isBandHedgeGridExhaustMode(hedgeMode)) return 'kinpoint';
  return normalizeBandDcaMode(dcaMode);
}

function parseBandDcaSteps(raw) {
  if (raw == null || raw === '') return createEmptyBandDcaSteps();
  let arr = raw;
  if (typeof raw === 'string') {
    try {
      arr = JSON.parse(raw);
    } catch {
      return createEmptyBandDcaSteps();
    }
  }
  if (!Array.isArray(arr)) return createEmptyBandDcaSteps();
  return Array.from({ length: BAND_DCA_STEP_COUNT }, (_, i) => {
    const step = arr[i] && typeof arr[i] === 'object' ? arr[i] : {};
    const usdt = Number(step.usdt);
    const gap_pct = Number(step.gap_pct ?? step.gap);
    return {
      usdt: Number.isFinite(usdt) && usdt >= 0 ? usdt : 0,
      gap_pct: Number.isFinite(gap_pct) && gap_pct >= 0 ? gap_pct : 0,
    };
  });
}

const isBandTurtleEntryBasis = (basis) => basis === 'turtle';

const bandFieldBlurStyle = (disabled) => (
  disabled ? { filter: 'blur(5px)', pointerEvents: 'none', opacity: 0.65 } : undefined
);

function formatBandRefPx(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n >= 1) return n.toFixed(4);
  return n.toFixed(8);
}

/** 킨포인트: 걸린 HL 트리거(DB) 우선, 없으면 N봉 HA. 터틀: HA(진입가)만 */
function formatBandHlHedgeShownPx(haPx, triggerPx, isTurtle) {
  if (isTurtle) return formatBandRefPx(haPx);
  const t = Number(triggerPx);
  if (Number.isFinite(t) && t > 0) return formatBandRefPx(t);
  return formatBandRefPx(haPx);
}

/** HA 고·저 극값 중간 — 극값평균 진입 필터 기준가 */
function formatBandExtremeAvgPx(highPx, lowPx) {
  const hi = Number(highPx);
  const lo = Number(lowPx);
  if (!Number.isFinite(hi) || !Number.isFinite(lo) || hi <= 0 || lo <= 0) return '0';
  return formatBandRefPx((hi + lo) / 2);
}

function parseBandRefPxInput(raw) {
  const trimmed = String(raw ?? '').trim().replace(/,/g, '');
  if (!trimmed) return 0;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : NaN;
}

const getCookieValue = (name) => {
  if (typeof document === 'undefined') {
    return '';
  }

  const targetCookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${name}=`));

  if (!targetCookie) {
    return '';
  }

  return decodeURIComponent(targetCookie.split('=').slice(1).join('='));
};

const getStoredBandAccessContext = () => {
  try {
    const storedValue = sessionStorage.getItem(BAND_ACCESS_STORAGE_KEY);
    if (!storedValue) {
      return { uid: '', from_admin: false };
    }

    const parsedValue = JSON.parse(storedValue);
    return {
      uid: String(parsedValue?.uid || ''),
      from_admin:
        parsedValue?.from_admin === 1 ||
        parsedValue?.from_admin === '1' ||
        parsedValue?.from_admin === true,
    };
  } catch (error) {
    return { uid: '', from_admin: false };
  }
};

const getInitialBandAccessContext = () => {
  const params = new URLSearchParams(window.location.search);
  const queryUid = params.get('uid') || '';
  const queryFromAdmin = params.get('from_admin') === '1';
  const adminTargetUidCookie = getCookieValue('admin_target_uid');

  if (queryUid || queryFromAdmin) {
    return {
      uid: queryUid || adminTargetUidCookie,
      from_admin: queryFromAdmin && Boolean(adminTargetUidCookie),
    };
  }

  const storedContext = getStoredBandAccessContext();
  if (!storedContext.from_admin) {
    return { uid: '', from_admin: false };
  }

  if (!adminTargetUidCookie) {
    return { uid: '', from_admin: false };
  }

  return {
    uid: adminTargetUidCookie || storedContext.uid,
    from_admin: true,
  };
};


export default function SetPageBand(){

    const [cookies] = useCookies(['uuid']);
    const [initialBandAccess] = useState(() => getInitialBandAccessContext());
    const [isAdminAccess, setIsAdminAccess] = useState(() => initialBandAccess.from_admin);
    const [targetUid, setTargetUid] = useState(() => initialBandAccess.uid || '');
    const [adminManualTradeAllowed, setAdminManualTradeAllowed] = useState(false);
    const [adminManualModal, setAdminManualModal] = useState(null);
    const [snapshotReloadToken, setSnapshotReloadToken] = useState(0);

    // ── Band 진입 제한 (심볼별, 관리자 전용) ──
    const HOLD_DAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'];
    const HOLD_RESTRICT_PRESETS = [
        { label: '토12~일21', start_day: 5, start_time: '12:00', end_day: 6, end_time: '21:00' },
        { label: '토00~일24', start_day: 5, start_time: '00:00', end_day: 6, end_time: '23:59' },
        { label: '금23~월06', start_day: 4, start_time: '23:00', end_day: 0, end_time: '06:00' },
        { label: '일00~일24', start_day: 6, start_time: '00:00', end_day: 6, end_time: '23:59' },
    ];
    const [bandRestrictModalOpen, setHoldRestrictModalOpen] = useState(false);
    const [bandRestrictDraft, setHoldRestrictDraft] = useState(null);
    const [bandRestrictSaveLoading, setHoldRestrictSaveLoading] = useState(false);
    const [bandRestrictSaveMsg, setHoldRestrictSaveMsg] = useState('');
    const [bandRestrictPresets, setHoldRestrictPresets] = useState([]);
    const [bandRestrictSelectedPresetId, setHoldRestrictSelectedPresetId] = useState('');
    const [bandRestrictPresetName, setHoldRestrictPresetName] = useState('');
    const [bandRestrictPresetMsg, setHoldRestrictPresetMsg] = useState('');

    const openBandRestrictModal = async () => {
        if (!isAdminAccess || !symbol || symbol === 'default') return;
        setHoldRestrictSaveMsg('');
        try {
            const res = await fetchBand(`/band/restrict_schedule?symbol=${encodeURIComponent(symbol)}`);
            const data = await res.json();
            setHoldRestrictDraft({
                restrict_enabled:    data.found ? !!data.restrict_enabled : false,
                restrict_start_day:  data.restrict_start_day  ?? 5,
                restrict_start_time: data.restrict_start_time ?? '12:00',
                restrict_end_day:    data.restrict_end_day    ?? 6,
                restrict_end_time:   data.restrict_end_time   ?? '21:00',
            });
        } catch {
            setHoldRestrictDraft({ restrict_enabled: false, restrict_start_day: 5, restrict_start_time: '12:00', restrict_end_day: 6, restrict_end_time: '21:00' });
        }
        // 프리셋 목록 로드
        fetchBand('/admin/band_restrict_presets')
            .then((r) => r.json())
            .then((data) => { if (Array.isArray(data)) setHoldRestrictPresets(data); })
            .catch(() => {});
        setHoldRestrictSelectedPresetId('');
        setHoldRestrictPresetName('');
        setHoldRestrictPresetMsg('');
        setHoldRestrictModalOpen(true);
    };

    const closeBandRestrictModal = () => {
        setHoldRestrictModalOpen(false);
        setHoldRestrictDraft(null);
        setHoldRestrictSaveMsg('');
        setHoldRestrictPresets([]);
        setHoldRestrictSelectedPresetId('');
        setHoldRestrictPresetName('');
        setHoldRestrictPresetMsg('');
    };

    const handleHoldRestrictDraftChange = (field) => (e) => {
        const val = field === 'restrict_enabled'
            ? e.target.checked
            : (field === 'restrict_start_day' || field === 'restrict_end_day')
                ? parseInt(e.target.value, 10)
                : e.target.value;
        setHoldRestrictDraft((prev) => ({ ...prev, [field]: val }));
    };

    const handleHoldRestrictSave = async () => {
        if (!bandRestrictDraft) return;
        setHoldRestrictSaveLoading(true);
        setHoldRestrictSaveMsg('');
        try {
            const res = await fetchBand('/band/restrict_schedule', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ symbol, both_mode, ...bandRestrictDraft }),
            });
            const data = await res.json();
            if (res.ok && data.ok) {
                setHoldRestrictSaveMsg('✅ 저장 완료');
                setTimeout(() => { setHoldRestrictSaveMsg(''); closeBandRestrictModal(); }, 1200);
            } else {
                setHoldRestrictSaveMsg(`❌ 저장 실패: ${data.error || res.status}`);
            }
        } catch (e) {
            setHoldRestrictSaveMsg(`❌ 오류: ${e.message}`);
        } finally {
            setHoldRestrictSaveLoading(false);
        }
    };
    // ─────────────────────────────────────────

    const showAdminManualTrade = useMemo(
        () => Boolean(isAdminAccess && adminManualTradeAllowed),
        [isAdminAccess, adminManualTradeAllowed]
    );

    // 쿠키에서 UUID를 이용하여 사용자 정보 가져오기
    const[uid,setUID] = useState(0);
    const[nickname,setNickname] = useState('');

    const [isChecked, setIsChecked] = useState(false);

    const [symbolList, setSymbolList] = useState([]);

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupAnchor, setPopupAnchor] = useState(null);
    const [isSymbolMenuOpen, setIsSymbolMenuOpen] = useState(false);
    const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);
    const [isBothMenuOpen, setIsBothMenuOpen] = useState(false);
    const symbolDropdownRef = useRef(null);
    const modeDropdownRef = useRef(null);
    const bothDropdownRef = useRef(null);
    const welfareRatioChangedByUser = useRef(false);
    const didInitSearch = useRef(false);
    const buildBandUrl = (url) => {
      const normalizedUid = String(targetUid ?? '').trim();
      const params = new URLSearchParams();

      if (normalizedUid) {
        params.set('uid', normalizedUid);
      }

      if (isAdminAccess) {
        params.set('from_admin', '1');
      }

      const query = params.toString();
      if (!query) {
        return url;
      }

      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}${query}`;
    };
    const fetchBand = (url, options = {}) =>
      fetch(buildBandUrl(url), {
        credentials: 'include',
        ...options,
      });

    useEffect(() => {
      if (isAdminAccess) {
        const nextContext = JSON.stringify({
          uid: targetUid || '',
          from_admin: 1,
        });
        sessionStorage.setItem(BAND_ACCESS_STORAGE_KEY, nextContext);
      } else {
        sessionStorage.removeItem(BAND_ACCESS_STORAGE_KEY);
      }

      if (window.location.search) {
        window.history.replaceState(window.history.state, document.title, window.location.pathname);
      }
    }, [isAdminAccess, targetUid]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (symbolDropdownRef.current && !symbolDropdownRef.current.contains(event.target)) {
          setIsSymbolMenuOpen(false);
        }
        if (modeDropdownRef.current && !modeDropdownRef.current.contains(event.target)) {
          setIsModeMenuOpen(false);
        }
        if (bothDropdownRef.current && !bothDropdownRef.current.contains(event.target)) {
          setIsBothMenuOpen(false);
        }
      };

      const handleEscapeKey = (event) => {
        if (event.key === 'Escape') {
          setIsSymbolMenuOpen(false);
          setIsModeMenuOpen(false);
          setIsBothMenuOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }, []);
  
    useEffect(() => {
      fetchUserInfo();
    }, [isAdminAccess]);

    const redirectToLogin = () => {
      alert('로그인 정보가 존재하지 않습니다. 로그인 페이지로 이동합니다.');
      window.location.href = isAdminAccess ? '/AdminLogin' : '/LoginAts';
    };
    
    useEffect(() => {
      if (!showAdminManualTrade) setAdminManualModal(null);
    }, [showAdminManualTrade]);

    const fetchUserInfo = () => {
      fetchBand(`/bandversion_selected`)
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(`[bandversion_selected] ${data.message || '페이지 접근 권한이 없습니다.'}`);
          }
          return data;
        })
        .then(data => {
          const nextIsAdminAccess = Boolean(data?.is_admin_access);
          setIsAdminAccess(nextIsAdminAccess);
          setTargetUid(String(data?.uid || ''));
          setAdminManualTradeAllowed(Boolean(data?.admin_manual_trade_allowed));

          if (!nextIsAdminAccess) {
            document.cookie = 'admin_target_uid=; path=/; max-age=0; SameSite=Lax';
          }

          if (data.message !== 'ok') {
            alert(data.message);
            window.location.href = nextIsAdminAccess ? '/Adminpage' : '/SelectPage';
            return;
          }

          // 권한 OK → 유저 정보 요청
          return fetchBand('/GetUserInfo');
        })
        .then(async (res) => {
          if (!res) {
            return null;
          }

          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(`[GetUserInfo] ${data.message || '사용자 정보 조회에 실패했습니다.'}`);
          }

          return data;
        })
        .then(user => {
          if (!user) {
            return;
          }
          const nextIsAdminAccess = Boolean(user?.is_admin_access);
          setIsAdminAccess(nextIsAdminAccess);
          setUID(user.uid);
          setTargetUid(String(user.uid || ''));
          setNickname(user.nickname);
          fetchLists();
        })
        .catch(err => {
          const message = err?.message || '로그인 정보가 존재하지 않습니다. 로그인 페이지로 이동합니다.';
          console.error('SetpageBand fetchUserInfo error:', err);
          alert(message);

          if (message.includes('접근권한')) {
            window.location.href = isAdminAccess ? '/Adminpage' : '/SelectPage';
            return;
          }

          window.location.href = isAdminAccess ? '/AdminLogin' : '/LoginAts';
        });
    };
    
    // selectedlevel, entry, waiting list 받아오기
    const fetchLists = () => {
      fetchBand(`/startcheck/searchlevel`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          if(data[0].super_user === "8"){
            setLevelCheck(data[0].super_user);  
          }else{
            setLevelCheck(data[0].selectedLevel);
          }
        })
        .catch(error => {
          console.error('Entry List 가져오기 오류:', error);
          alert(error);
        });

      fetchBand(`/startcheck_band/list_entry`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          updateList(data, e_items, setE_Items);
        })
        .catch(error => {
          console.error('Entry List 가져오기 오류:', error);
          alert(error);
        });
  
      fetchBand(`/startcheck_band/list_wait`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          updateList(data, w_items, setW_Items);
        })
        .catch(error => {
          console.error('Waiting List 가져오기 오류:', error);
          alert(error);
        });
    };
  
    const updateList = (data, list, setList) => {
      for (let i = 0; i < data.length; i++) {
        if (!isItemInList(data[i].symbol, list)) {
          setList(prevList => [...prevList, data[i].symbol]);
        }
      }
    };

    const isItemInList = (item, list) => {
      return list.includes(item);
    };

    const[seed,setSeed] = useState('basic');

    const[both_mode,setBothMode] = useState('single');
    const [snapshotHasSecondAccount, setSnapshotHasSecondAccount] = useState(false);

    const[mode,setMode] = useState('normal');

    const[auto_count,setSymbolCount] = useState(3);
    const[top_bottom,setTopBottom] = useState('top');
    const[fluctuation,setFluctuation] = useState(0);

    const[funding_rate,setFundingRate] = useState('disable');
    const[f_percent_low,setFpercentLow] = useState(-100);
    const[f_percent_high,setFpercentHigh] = useState(0);

    const[symbol,setSymbol] = useState('default');
    const selectableSymbols = useMemo(
      () => Array.from(new Set(symbolList.filter((sym) => sym && sym !== 'default'))),
      [symbolList]
    );
    const currentSymbolLabel = symbol && symbol !== 'default' ? symbol : 'symbol select';
    const MODE_OPTIONS = [
      { value: 'normal', label: '일반모드 셋팅' },
    ];
    const currentModeLabel = MODE_OPTIONS.find((option) => option.value === mode)?.label || '일반모드 셋팅';

    const BOTH_MODE_OPTIONS = [
      { value: 'single', label: 'single mode' },
      { value: 'double', label: 'double mode' },
    ];
    const currentBothModeLabel = BOTH_MODE_OPTIONS.find((option) => option.value === both_mode)?.label || 'single mode';
    const isAutoMode = mode === 'auto';
    const autoDisabled = mode !== 'auto';
    const autoFieldStyle = autoDisabled
      ? { filter: 'blur(5px)', pointerEvents: 'none', opacity: 0.5 }
      : {};

    useEffect(() => {
      if (mode === 'auto') {
        setIsSymbolMenuOpen(false);
      }
    }, [mode]);

    const[full_mode,setFullMode] = useState('extension');
    const[hedge_hl_bar_count, setHedgeHlBarCount] = useState(BAND_DEFAULT_HEDGE_HL_BAR_COUNT);
    const[hedgeHlHighPx, setHedgeHlHighPx] = useState('0');
    const[hedgeHlLowPx, setHedgeHlLowPx] = useState('0');
    const[bandHlLongTriggerPx, setBandHlLongTriggerPx] = useState('0');
    const[bandHlShortTriggerPx, setBandHlShortTriggerPx] = useState('0');

    const[leverage_info, setLeverage] = useState(20);

    const[sensitivity,setSensitivity] = useState(2);
    const[atr_period,setAtrPeriod] = useState(2);
    const[sensitivity_short,setSensitivityShort] = useState(2);
    const[atr_period_short,setAtrPeriodShort] = useState(2);

    const entry_mode = 'kinpoint';
    const [entry_basis, setEntryBasis] = useState('kinpoint');
    const hedgeHlHighDisplay = useMemo(
      () => formatBandHlHedgeShownPx(hedgeHlHighPx, bandHlLongTriggerPx, isBandTurtleEntryBasis(entry_basis)),
      [hedgeHlHighPx, bandHlLongTriggerPx, entry_basis],
    );
    const hedgeHlLowDisplay = useMemo(
      () => formatBandHlHedgeShownPx(hedgeHlLowPx, bandHlShortTriggerPx, isBandTurtleEntryBasis(entry_basis)),
      [hedgeHlLowPx, bandHlShortTriggerPx, entry_basis],
    );
    const hedgeExtremeAvgPx = useMemo(
      () => formatBandExtremeAvgPx(hedgeHlHighPx, hedgeHlLowPx),
      [hedgeHlHighPx, hedgeHlLowPx],
    );
    const[close_mode,setCloseMode] = useState('kinpoint');
    const[close_tp_hl_bar_count, setCloseTpHlBarCount] = useState(BAND_DEFAULT_CLOSE_TP_HL_BAR_COUNT);
    const[closeHlHighPx, setCloseHlHighPx] = useState('0');
    const[closeHlLowPx, setCloseHlLowPx] = useState('0');
    const[hedge_mode,setHedgeMode] = useState('kinpoint');

    const[hedge_out_checking,setHedgeOutChecking] = useState(-1);
    const[hedge_out_candle,setHedgeOutCandle] = useState('1h');

    const[entry_standard,setEntryStandard] = useState('ema');
    const[candle_standard,setCandleStandard] = useState('4h');
    const[candle_type_standard, setCandleTypeStandard] = useState('haikin');
    const[sensitivity_standard,setSensitivityStandard] = useState(2);
    const[atr_period_standard,setAtrPeriodStandard] = useState(2);

    const[ema_standard,setEmaStandard] = useState(200);
    /** 최초진입 2nd 보조기준 */
    const[entry_standard_2nd,setEntryStandard2nd] = useState('disable');
    const[candle_standard_2nd,setCandleStandard2nd] = useState('4h');
    const[candle_type_standard_2nd,setCandleTypeStandard2nd] = useState('haikin');
    const[sensitivity_standard_2nd,setSensitivityStandard2nd] = useState(2);
    const[atr_period_standard_2nd,setAtrPeriodStandard2nd] = useState(2);
    const[ema_standard_2nd,setEmaStandard2nd] = useState(200);
    
    const[ema_losscut,setEmaLosscut] = useState('no');
    const[ema_hedge_gap_ratio,setEmaHedgeGapRatio] = useState(0);
    //////////////////////////////////////////////////////////
    const[circle_entry_candle,setCircleEntryCandle] = useState('1h');
    const[sensitivity_circle_entry,setSensitivityCircleEntry] = useState(1);
    const[atr_period_circle_entry,setAtrPeriodCircleEntry] = useState(20);
    const[sensitivity_circle_entry_short,setSensitivityCircleEntryShort] = useState(1);
    const[atr_period_circle_entry_short,setAtrPeriodCircleEntryShort] = useState(20);
    const[candle_type_circle_entry, setCandleTypeCircleEntry] = useState('haikin');
    const[circle_entry_checking,setCircleEntryChecking] = useState(-1);
    const[circle_entry_mode,setCircleEntryMode] = useState('none');
    /////////////circle hedge///////////////////////////////
    const[circle_hedge_checking,setCircleHedgeChecking] = useState(-1);
    const[circle_hedge_mode,setCircleHedgeMode] = useState('none');
    const[circle_hedge_candle,setCircleHedgeCandle] = useState('1h');
    //////////////////////////////////////////////////////////
    const[circle_candle,setCircleCandle] = useState('1h');
    const[sensitivity_circle,setSensitivityCircle] = useState(1);
    const[atr_period_circle,setAtrPeriodCircle] = useState(20);
    const[sensitivity_circle_short,setSensitivityCircleShort] = useState(1);
    const[atr_period_circle_short,setAtrPeriodCircleShort] = useState(20);
    const[candle_type_circle, setCandleTypeCircle] = useState('haikin');
    const[circle_checking,setCircleChecking] = useState(-1);
    const[circle_close_mode,setCircleCloseMode] = useState('none');
    const[circle_benefit_mode,setCircleBenefitMode] = useState('disable');
    ///////////////////////////////////////////////////
    
    const[entry_candle,setEntryCandle] = useState('1h');

    const[entry_checking,setEntryChecking] = useState(-1);
    const[standard_checking,setStandardChecking] = useState(-1);
    const[close_checking,setCloseChecking] = useState(-1);

    const[start_ratio,setStartRatio] = useState(14);
    const[confirmed_ratio,setConfirmedRatio] = useState(50);
    const[max_ratio,setMaxRatio] = useState(50);

    const[hedge_candle,setHedgeCandle] = useState('1h');
    const[entry_extreme_avg_mode,setEntryExtremeAvgMode] = useState('disable');
    const[band_dca_mode, setBandDcaMode] = useState('disable');
    const[band_dca_steps, setBandDcaSteps] = useState(createEmptyBandDcaSteps);
    const[hedge_checking,setHedgeChecking] = useState(-1);
    const[band_max_losscut_pct, setBandMaxLosscutPct] = useState(0);
    const[bandMaxLosscutPctValid, setBandMaxLosscutPctValid] = useState(true);

    const[entry_hold_mode,setEntryHoldMode] = useState('disable');
    const[entry_hold_ratio,setEntryHoldRatio] = useState(0);
    const[entry_hold_roi_pct,setEntryHoldRoiPct] = useState(0);
    const[entry_hold_switch_pct,setEntryHoldSwitchPct] = useState(0);
    const[hedge_hold_mode,setHedgeHoldMode] = useState('disable');
    const[hedge_hold_ratio,setHedgeHoldRatio] = useState(0);
    const[hedge_hold_roi_pct,setHedgeHoldRoiPct] = useState(0);
    const[hedge_hold_switch_pct,setHedgeHoldSwitchPct] = useState(0);
    //////////////////////////////////////////////////////////

    const[close_candle,setCloseCandle] = useState('1h');
    const[sensitivity_close,setSensitivityClose] = useState(1);
    const[atr_period_close,setAtrPeriodClose] = useState(1);
    const[sensitivity_close_short,setSensitivityCloseShort] = useState(1);
    const[atr_period_close_short,setAtrPeriodCloseShort] = useState(1);

    const[direction,setDirection] = useState('long');

    //////////////////////////////////////////////////////////

    const[candle_type_entry, setCandleTypeEntry] = useState('haikin');
    const[candle_type_close, setCandleTypeClose] = useState('haikin');

    const[benefit_ratio,setBenefitRatio] = useState(0.3);

    const[trigger_mode,setTriggerMode] = useState('no');
    const[trigger_ratio,setTriggerRatio] = useState(1);

    const[main_balance,setMainBalance] = useState(0);
    const[second_balance,setSecondBalance] = useState(0);

    const[total_benefit_usdt,setTotalBenefitUsdt] = useState(0);
    const[total_benefit_usdt_obj,setTotalBenefitUsdtObj] = useState(0);
    const[total_benefit_count,setTotalBenefitCount] = useState(0);
    const[total_current_count,setTotalCurrentCount] = useState(0);
    const[total_benefit_start,setTotalBenefitStart] = useState(0);
    const[total_continue_mode,setTotalContinueMode] = useState('disable');
    const[total_continue_direction,setTotalContinueDirection] = useState('both');
    const[single_tp_continue_mode,setSingleTpContinueMode] = useState('disable');


    const[entry_usdt_1, setEntryUsdt_1] = useState(10);
    const[welfare_mode, setWelfareMode] = useState('disable');
    const[welfare_ratio, setWelfareRatio] = useState(100);

    const[circle_mode,setCircleMode] = useState('disable');
    const[circle_rate,setCircleRate] = useState(0);
    const[circle_rate_interval,setCircleRateInterval] = useState(0);

    const[double_benefit, setDoubleBenefit] = useState(0);
    const[double_pnl, setDoublePnl] = useState(0);
    const[double_continue, setDoubleContinue] = useState('disable');

    const[totalpnl,setTotalPnl] = useState(0);

    const[hedge_continue,setHedgeContinue] = useState('no');

    //////////////////////////////////////////////////////////

    const[symbolCountValid, setSymbolCountValid] = useState(true);
    const[fPercentLowValid, setFpercentLowValid] = useState(true);
    const[fPercentHighValid, setFpercentHighValid] = useState(true);
    const[fluctuationValid, setFluctuationValid] = useState(true);
    
    const[leverageValid, setLeverageValid] = useState(true);

    const[emaStandardValid, setEmaStandardValid] = useState(true);

    const[sensitivityStandardValid, setSensitivityStandardValid] = useState(true);
    const[atrperiodStandardValid, setAtrperiodStandardValid] = useState(true);

    const[emaStandard2ndValid, setEmaStandard2ndValid] = useState(true);
    const[sensitivityStandard2ndValid, setSensitivityStandard2ndValid] = useState(true);
    const[atrperiodStandard2ndValid, setAtrperiodStandard2ndValid] = useState(true);

    const[sensitivityValid, setSensitivityValid] = useState(true);
    const[atrperiodValid, setAtrperiodValid] = useState(true);

    const[sensitivityShortValid, setSensitivityShortValid] = useState(true);
    const[atrperiodShortValid, setAtrperiodShortValid] = useState(true);
  
    const[sensitivityCircleValid, setSensitivityCircleValid] = useState(true);
    const[atrperiodCircleValid, setAtrperiodCircleValid] = useState(true);
    const[sensitivityCircleShortValid, setSensitivityCircleShortValid] = useState(true);
    const[atrperiodCircleShortValid, setAtrperiodCircleShortValid] = useState(true);

    const[sensitivityCircleEntryValid, setSensitivityCircleEntryValid] = useState(true);
    const[atrperiodCircleEntryValid, setAtrperiodCircleEntryValid] = useState(true);
    const[sensitivityCircleEntryShortValid, setSensitivityCircleEntryShortValid] = useState(true);
    const[atrperiodCircleEntryShortValid, setAtrperiodCircleEntryShortValid] = useState(true);

    const[sensitivityCloseValid, setSensitivityCloseValid] = useState(true);
    const[atrperiodCloseValid, setAtrperiodCloseValid] = useState(true);
    const[sensitivityCloseShortValid, setSensitivityCloseShortValid] = useState(true);
    const[atrperiodCloseShortValid, setAtrperiodCloseShortValid] = useState(true);

    const[benefit_ratioratioValid, setBenefitRatioratioValid] = useState(true);

    const[start_ratioValid, setStartRatioValid] = useState(true);
    const[confirmed_ratioValid, setConfirmedRatioValid] = useState(true);
    const[max_ratioValid, setMaxRatioValid] = useState(true);

    const[total_benefit_usdtValid, setTotalBenefitusdtValid] = useState(true);
    const[total_benefit_countValid, setTotalBenefitCountValid] = useState(true);

    const[trigger_ratioValid, setTriggerRatioValid] = useState(true);

    const[welfare_ratioValid, setWelfareRatioValid] = useState(true);

    const[entryusdtValid_1,setEntryUsdtValid_1] = useState(true);

    const[circle_rateValid,setCircleRateValid] = useState(true);
    const[circle_rate_intervalValid,setCircleRateIntervalValid] = useState(true);

    const[double_benefitValid, setDoubleBenefitValid] = useState(true);

    const[entry_hold_ratioValid, setEntryHoldRatioValid] = useState(true);
    const[hedge_hold_ratioValid, setHedgeHoldRatioValid] = useState(true);

    const[setlevel, setLevelCheck] = useState(0);

    const[notAllow, setNotAllow] = useState(true);

    const[e_items, setE_Items] = useState([]);
    const[w_items, setW_Items] = useState([]);

    const toggleModeMenu = () => {
      setIsModeMenuOpen((prev) => !prev);
    };

    const handleModeSelect = (value) => {
      setIsModeMenuOpen(false);
      if (value === mode) {
        return;
      }
      setMode(value);
      if (value === 'auto') {
        onClickSearch_auto();
      }
    };

    const toggleSymbolMenu = () => {
      if (mode === 'auto') {
        return;
      }
      setIsSymbolMenuOpen((prev) => !prev);
    };

    const handleSymbolSelect = (value) => {
      if (mode === 'auto') {
        return;
      }
      setSymbol(value);
      if (value === 'default') {
        clearBandHedgeHlDisplay();
        setIsSymbolMenuOpen(false);
        return;
      }
      if (value) {
        onClickSearch(value);
      }
      setIsSymbolMenuOpen(false);
    };

    const toggleBothMenu = () => {
      setIsBothMenuOpen((prev) => !prev);
    };

    const handleBothMenuSelect = async (value) => {
      if (value === "double") {
        const valid = await validateSecondApi();
        if (!valid) {
          setIsBothMenuOpen(false);
          return;
        }
      }
      handleBothMode(value);
      setIsBothMenuOpen(false);
    };

    const handleCheckboxChange = (event) => {
      setIsChecked(event.target.checked);
    };

    const applyTotalBenefitStart = (value) => {
      if (value !== undefined && value !== null) {
        setTotalBenefitStart(value);
      }
    };

    const[trade_start,setTradeStart] = useState(false);
    const[benefit_stop,setBenefit_stop] = useState(false);
    const[benefit_cancel,setBenefit_cancel] = useState(false);

    const[currentCoin_stop,setCurrentCoin_stop] = useState(false);
    const[allCoin_stop,setAllCoin_stop] = useState(false);

    const[end,setEnd] = useState(false);
    
    // 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //

    const validateSecondApi = async () => {
      try {
        const response = await fetchBand("/hedge/validate-second");
        const text = await response.text();
        let payload;
        try {
          payload = JSON.parse(text);
        } catch {
          payload = { message: text };
        }
        if (!response.ok || !payload?.ok) {
          throw new Error(payload.message || "SelectPage에서 유효한 2nd API 키를 등록하세요.");
        }
        if (payload.message) {
          alert(payload.message);
        }
        return true;
      } catch (error) {
        console.error(error);
        alert(error.message || "SelectPage에서 유효한 2nd API 키를 등록하세요.");
        return false;
      }
    };

    const handleBothMode = (input) => {
      const nextValue = typeof input === 'string' ? input : input?.target?.value;
      if (!nextValue) {
        return;
      }
      setBothMode(nextValue);
    };

    const handleSymbolCount = (e) => {
      const newcount = e.target.value.trim();
      setSymbolCount(newcount);
        const regex = 
        /^\d*$/;
        if (regex.test(newcount)) {
          setSymbolCountValid(true);
          } 
        else {
          setSymbolCountValid(false);
          }
        };

    const handleTopBotton = (e) => {
      setTopBottom(e.target.value);
        };

    const handleFundingRate = (e) => {
      setFundingRate(e.target.value);
        };

    const handleFpercentLow = (e) => {
      const newPercent = e.target.value.trim();
      setFpercentLow(newPercent);

      const regex = /^-?\d*(\.\d+)?$/; // ✅ 정수와 실수(음수 포함) 허용
      if (regex.test(newPercent)) {
        setFpercentLowValid(true);
      } else {
        setFpercentLowValid(false);
      }
    };

    const handleFpercentHigh = (e) => {
      const newPercent = e.target.value.trim();
      setFpercentHigh(newPercent);

      const regex = /^-?\d*(\.\d+)?$/; // ✅ 정수와 실수(음수 포함) 허용
      if (regex.test(newPercent)) {
        setFpercentHighValid(true);
      } else {
        setFpercentHighValid(false);
      }
    };

    const handleFluctuation = (e) => {
      const newPercent = e.target.value.trim();
      setFluctuation(newPercent);

      const regex = /^-?\d*(\.\d+)?$/; // ✅ 정수와 실수(음수 포함) 허용
      if (regex.test(newPercent)) {
        setFluctuationValid(true);
      } else {
        setFluctuationValid(false);
      }
    };

    const handleFullMode = (e) => {
      setFullMode(e.target.value);
        };

    const clearBandHedgeHlDisplay = () => {
      setHedgeHlHighPx('0');
      setHedgeHlLowPx('0');
      setBandHlLongTriggerPx('0');
      setBandHlShortTriggerPx('0');
    };

    const applyBandHedgeHlFromRow = (row) => {
      if (!row) return;
      setHedgeHlBarCount(Math.max(1, parseInt(row.hedge_hl_bar_count, 10) || BAND_DEFAULT_HEDGE_HL_BAR_COUNT));
      setHedgeHlHighPx(formatBandRefPx(row.hedge_hl_high_px));
      setHedgeHlLowPx(formatBandRefPx(row.hedge_hl_low_px));
      setBandHlLongTriggerPx(formatBandRefPx(row.band_hl_long_trigger_px));
      setBandHlShortTriggerPx(formatBandRefPx(row.band_hl_short_trigger_px));
    };

    const applyBandCloseHlFromRow = (row) => {
      if (!row) return;
      setCloseTpHlBarCount(Math.max(1, parseInt(row.close_tp_hl_bar_count, 10) || BAND_DEFAULT_CLOSE_TP_HL_BAR_COUNT));
      setCloseHlHighPx(formatBandRefPx(row.close_hl_high_px));
      setCloseHlLowPx(formatBandRefPx(row.close_hl_low_px));
    };

    const handleCloseMode = (e) => {
      setCloseMode(e.target.value === 'extreme_hl' ? 'extreme_hl' : 'kinpoint');
    };

    const handleCloseTpHlBarCount = (e) => {
      const raw = e.target.value.trim();
      if (raw === '') {
        setCloseTpHlBarCount('');
        return;
      }
      if (!/^\d+$/.test(raw)) {
        return;
      }
      const num = parseInt(raw, 10);
      setCloseTpHlBarCount(Math.min(Math.max(num, 1), 999));
    };

    const resolveCloseTpHlBarCount = () => {
      const num = parseInt(close_tp_hl_bar_count, 10);
      return Number.isFinite(num) && num >= 1 ? Math.min(num, 999) : BAND_DEFAULT_CLOSE_TP_HL_BAR_COUNT;
    };

    const handleHedgeHlBarCount = (e) => {
      const raw = e.target.value.trim();
      if (raw === '') {
        setHedgeHlBarCount('');
        return;
      }
      if (!/^\d+$/.test(raw)) {
        return;
      }
      const num = parseInt(raw, 10);
      setHedgeHlBarCount(Math.min(Math.max(num, 1), 999));
    };

    const resolveHedgeHlBarCount = () => {
      const num = parseInt(hedge_hl_bar_count, 10);
      return Number.isFinite(num) && num >= 1 ? Math.min(num, 999) : BAND_DEFAULT_HEDGE_HL_BAR_COUNT;
    };


    
    const handleHedgeMode = (e) => {
      const next = e.target.value;
      setHedgeMode(next);
      if (isBandHedgeGridExhaustMode(next)) {
        setBandDcaMode('kinpoint');
      }
    };

    const handleHedgeOutChecking = (e) => {
      setHedgeOutChecking(e.target.value);
        };

    const handleHedgeOutCandle = (e) => {
      setHedgeOutCandle(e.target.value);
        };

    const handleEntryCandle = (e) => {
      const v = e.target.value;
      setEntryCandle(v);
      setHedgeCandle(v);
        };

    const handleEntryBasis = (e) => {
      const next = e.target.value === 'turtle' ? 'turtle' : 'kinpoint';
      setEntryBasis(next);
      if (next === 'turtle') {
        setBenefitRatio(0);
        setEntryHoldRatio(0);
        setEntryHoldRoiPct(0);
      }
    };

    const handleEntryExtremeAvgMode = (e) => {
      setEntryExtremeAvgMode(e.target.value);
    };

    const handleBandDcaMode = (e) => {
      setBandDcaMode(normalizeBandDcaMode(e.target.value));
    };

    const handleBandDcaStepChange = (index, field, rawValue) => {
      setBandDcaSteps((prev) => {
        const next = prev.map((step) => ({ ...step }));
        const trimmed = String(rawValue ?? '').trim();
        if (trimmed === '') {
          next[index] = { ...next[index], [field]: 0 };
          return next;
        }
        const num = Number(trimmed);
        next[index] = {
          ...next[index],
          [field]: Number.isFinite(num) && num >= 0 ? num : 0,
        };
        return next;
      });
    };

    const handleEntryStandard = (e) => {
      setEntryStandard(e.target.value);
    };

    const handleCandleStandard = (e) => {
      setCandleStandard(e.target.value);
    };

    const handleCandleTypeStandard = (e) => {
      setCandleTypeStandard(e.target.value);
    };

    const handleEmaLosscut = (e) => {
      setEmaLosscut(e.target.value);
        };

    const handleEmaHedgeGapRatio = (e) => {
      const v = parseFloat(e.target.value);
      setEmaHedgeGapRatio(Number.isFinite(v) ? v : 0);
    };

    const handleEntryChecking = (e) => {
      setEntryChecking(e.target.value);
        };

    const handleStandardChecking = (e) => {
      setStandardChecking(e.target.value);
        };
    
    const handleEntryHoldMode = (e) => {
      setEntryHoldMode(e.target.value);
        };

    const handleEntryHoldRoiPct = (e) => {
      applyDecimalPctInputChange(e.target.value, setEntryHoldRoiPct);
    };

    const handleEntryHoldSwitchPct = (e) => {
      applyDecimalPctInputChange(e.target.value, setEntryHoldSwitchPct);
    };

    const handleHedgeHoldMode = (e) => {
      setHedgeHoldMode(e.target.value);
        };

    const handleHedgeHoldRoiPct = (e) => {
      applyDecimalPctInputChange(e.target.value, setHedgeHoldRoiPct);
    };

    const handleHedgeHoldSwitchPct = (e) => {
      applyDecimalPctInputChange(e.target.value, setHedgeHoldSwitchPct);
    };

    const handleCircleChecking = (e) => {
      setCircleChecking(e.target.value);
        };
    
    const handleCloseChecking = (e) => {
      setCloseChecking(e.target.value);
        };

    const handleCircleCloseMode = (e) => {
      setCircleCloseMode(e.target.value);
        };
    
    const handleCircleEntryCandle = (e) => {
      setCircleEntryCandle(e.target.value);
        };
    
    const handleCircleHedgeMode = (e) => {
      setCircleHedgeMode(e.target.value);
        };
    
    const handleCircleHedgeCandle = (e) => {
      setCircleHedgeCandle(e.target.value);
        };
    
    const handleCircleHedgeChecking = (e) => {
      setCircleHedgeChecking(e.target.value);
        };
    
    const handleCandleTypeCircleEntry = (e) => {
      setCandleTypeCircleEntry(e.target.value);
        };

    const handleCircleEntryChecking = (e) => {
      setCircleEntryChecking(e.target.value);
        };

    const handleCircleEntryMode = (e) => {
      setCircleEntryMode(e.target.value);
        };

    const handleCircleCandle = (e) => {
      setCircleCandle(e.target.value);
        };
    
    const handleCircleBenefitMode = (e) => {
      setCircleBenefitMode(e.target.value);
        };

    const handleCloseCandle = (e) => {
      setCloseCandle(e.target.value);
        };

    const handleHedgeChecking = (e) => {
      setHedgeChecking(e.target.value);
        };

    const handleBandMaxLosscutPct = (e) => {
      const val = e.target.value.trim();
      setBandMaxLosscutPct(val);
      const regex = /^\d*\.?\d*$/;
      setBandMaxLosscutPctValid(regex.test(val));
    };
            
    const handleHedgeCandle = (e) => {
      setHedgeCandle(e.target.value);
        };
    
    const handleDirection = (e) => {
      setDirection(e.target.value);
        };
    
    const handleCandleTypeEntry = (e) => {
      setCandleTypeEntry(e.target.value);
        };

    
    const handleCandleTypeCircle = (e) => {
      setCandleTypeCircle(e.target.value);
        };

    const handleCandleTypeClose = (e) => {
      setCandleTypeClose(e.target.value);
        };
    
    const handleCircleMode = (e) => {
      setCircleMode(e.target.value);
        };

    const handleHedgeContinue = (e) => {
      setHedgeContinue(e.target.value);
        };

    const handleTriggerMode = (e) => {
      setTriggerMode(e.target.value);
        };

    const handleWelfareMode = (e) => {
      setWelfareMode(e.target.value);
        };

    const handleDoubleContinue = (e) => {
      setDoubleContinue(e.target.value);
        };
      
    const handleTotalContinueMode = (e) => {
      setTotalContinueMode(e.target.value);
        };
    
    const handleTotalContinueDirection = (e) => {
      setTotalContinueDirection(e.target.value);
        };

    const handleSingleTpContinueMode = (e) => {
      setSingleTpContinueMode(e.target.value);
        };
        
    const handleLeverage = (e) => {
      const newLeverage = e.target.value.trim();
      setLeverage(newLeverage);
        const regex = 
        /^\d*$/;
        if (regex.test(newLeverage)) {
          setLeverageValid(true);
          } 
        else {
          setLeverageValid(false);
          }
        };

    const handleSensitivity = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivity(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityValid(true);
          } 
        else {
          setSensitivityValid(false);
          }
        };
    
    const handleAtrperiod = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriod(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodValid(true);
          } 
        else {
          setAtrperiodValid(false);
          }
        };
      
    const handleSensitivityShort = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityShort(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityShortValid(true);
          } 
        else {
          setSensitivityShortValid(false);
          }
        };
    
    const handleAtrperiodShort = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodShort(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodShortValid(true);
          } 
        else {
          setAtrperiodShortValid(false);
          }
        };

    const handleEmaStandard = (e) => {
      const newemastandard = e.target.value.trim();
      setEmaStandard(newemastandard);
        const regex = 
        /^\d*$/;
        if (regex.test(newemastandard)) {
          setEmaStandardValid(true);
          } 
        else {
          setEmaStandardValid(false);
          }
        };

    const handleSensitivityStandard = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityStandard(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityStandardValid(true);
          } 
        else {
          setSensitivityStandardValid(false);
          }
        };
    
    const handleAtrperiodStandard = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodStandard(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodStandardValid(true);
          } 
        else {
          setAtrperiodStandardValid(false);
          }
        };

    const handleEntryStandard_2nd = (e) => {
      setEntryStandard2nd(e.target.value);
    };

    const handleCandleStandard_2nd = (e) => {
      setCandleStandard2nd(e.target.value);
    };

    const handleCandleTypeStandard_2nd = (e) => {
      setCandleTypeStandard2nd(e.target.value);
    };

    const handleEmaStandard_2nd = (e) => {
      const v = e.target.value.trim();
      setEmaStandard2nd(v);
      const regex = /^\d*$/;
      setEmaStandard2ndValid(regex.test(v));
    };

    const handleSensitivityStandard_2nd = (e) => {
      const v = e.target.value.trim();
      setSensitivityStandard2nd(v);
      const regex = /^\d+$/;
      setSensitivityStandard2ndValid(regex.test(v));
    };

    const handleAtrperiodStandard_2nd = (e) => {
      const v = e.target.value.trim();
      setAtrPeriodStandard2nd(v);
      const regex = /^\d+$/;
      setAtrperiodStandard2ndValid(regex.test(v));
    };

    const handleSensitivityCircle = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityCircle(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCircleValid(true);
          } 
        else {
          setSensitivityCircleValid(false);
          }
        };
      
    const handleAtrperiodCircle = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodCircle(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCircleValid(true);
          } 
        else {
          setAtrperiodCircleValid(false);
          }
        };

    const handleSensitivityCircleShort = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityCircleShort(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCircleShortValid(true);
          } 
        else {
          setSensitivityCircleShortValid(false);
          }
        };
    
    const handleAtrperiodCircleShort = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodCircleShort(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCircleShortValid(true);
          } 
        else {
          setAtrperiodCircleShortValid(false);
          }
        };

    const handleSensitivityCircleEntry = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityCircleEntry(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCircleEntryValid(true);
          } 
        else {
          setSensitivityCircleEntryValid(false);
          }
        };
      
    const handleAtrperiodCircleEntry = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodCircleEntry(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCircleEntryValid(true);
          } 
        else {
          setAtrperiodCircleEntryValid(false);
          }
        };

    const handleSensitivityCircleEntryShort = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityCircleEntryShort(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCircleEntryShortValid(true);
          } 
        else {
          setSensitivityCircleEntryShortValid(false);
          }
        };
      
    const handleAtrperiodCircleEntryShort = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodCircleEntryShort(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCircleEntryShortValid(true);
          } 
        else {
          setAtrperiodCircleEntryShortValid(false);
          }
        };

    const handleSensitivityClose = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityClose(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCloseValid(true);
          } 
        else {
          setSensitivityCloseValid(false);
          }
        };
    
    const handleAtrperiodClose = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodClose(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCloseValid(true);
          } 
        else {
          setAtrperiodCloseValid(false);
          }
        };

    const handleSensitivityCloseShort = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityCloseShort(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCloseShortValid(true);
          } 
        else {
          setSensitivityCloseShortValid(false);
          }
        };
    
    const handleAtrperiodCloseShort = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodCloseShort(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCloseShortValid(true);
          } 
        else {
          setAtrperiodCloseShortValid(false);
          }
        };

    const handleBenefitratio = (e) => {
      const newBenefitratio = e.target.value.trim();
      setBenefitRatio(newBenefitratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newBenefitratio)) {
          setBenefitRatioratioValid(true);
          } 
        else {
          setBenefitRatioratioValid(false);
          }
        };

    const handleCircleRatio = (e) => {
      const newCircleratio = e.target.value.trim();
      setCircleRate(newCircleratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newCircleratio)) {
          setCircleRateValid(true);
          } 
        else {
          setCircleRateValid(false);
          }
        };
    
    const handleCircleRateInterval = (e) => {
      const newCircleRateInterval = e.target.value.trim();
      setCircleRateInterval(newCircleRateInterval);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newCircleRateInterval)) {
          setCircleRateIntervalValid(true);
          } 
        else {
          setCircleRateIntervalValid(false);
          }
        };
    
    const handleTriggerratio = (e) => {
      const newTriggerratio = e.target.value.trim();
      setTriggerRatio(newTriggerratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newTriggerratio)) {
          setTriggerRatioValid(true);
          } 
        else {
          setTriggerRatioValid(false);
          }
        };
    
    const handleWelfareRatio = (e) => {
      const newWelfareRatio = e.target.value.trim();
      setWelfareRatio(newWelfareRatio);
      welfareRatioChangedByUser.current = true;
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newWelfareRatio)) {
          setWelfareRatioValid(true);
          } 
        else {
          setWelfareRatioValid(false);
          }
        };

    const handleTotalBenefitCount = (e) => {
      const newTotalBenefitCount = e.target.value.trim();
      setTotalBenefitCount(newTotalBenefitCount);
        const regex = 
        /^\d+$/;
        if (regex.test(newTotalBenefitCount)) {
          setTotalBenefitCountValid(true);
          } 
        else {
          setTotalBenefitCountValid(false);
          }
        };
      
    const handleStartRatio = (e) => {
      const newStartratio = e.target.value.trim();
      setStartRatio(newStartratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newStartratio)) {
          setStartRatioValid(true);
          } 
        else {
          setStartRatioValid(false);
          }
        };
    
    const handleConfirmedRatio = (e) => {
      const newConfirmedratio = e.target.value.trim();
      setConfirmedRatio(newConfirmedratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newConfirmedratio)) {
          setConfirmedRatioValid(true);
          } 
        else {
          setConfirmedRatioValid(false);
          }
        };

    const handleMaxRatio = (e) => {
      const newMaxratio = e.target.value.trim();
      setMaxRatio(newMaxratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newMaxratio)) {
          setMaxRatioValid(true);
          } 
        else {
          setMaxRatioValid(false);
          }
        };

    /////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////

    // 진입 USDT 유효성 검사 //// 진입 USDT 유효성 검사 //// 진입 USDT 유효성 검사 //
    const handleEntryUsdt_1 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_1(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_1(true);
          } 
        else {
          setEntryUsdtValid_1(false);
          }
        };
  
    const handleDoubleBenefit = (e) => {
      const newEntrybenefit = e.target.value.trim();
      setDoubleBenefit(newEntrybenefit);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntrybenefit)) {
          setDoubleBenefitValid(true);
          } 
        else {
          setDoubleBenefitValid(false);
          }
        };
    
    const handleTotalBenefitusdt = (e) => {
      const newTotalBenefitusdt = e.target.value.trim();
      setTotalBenefitUsdt(newTotalBenefitusdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newTotalBenefitusdt)) {
          setTotalBenefitusdtValid(true);
          } 
        else {
          setTotalBenefitusdtValid(false);
          }
        };
    
    const resetEntryHoldPreserveUiDefaults = () => {
      setEntryHoldMode('disable');
      setEntryHoldRoiPct(0);
      setEntryHoldSwitchPct(0);
    };

    const resetHedgeHoldPreserveUiDefaults = () => {
      setHedgeHoldMode('disable');
      setHedgeHoldRoiPct(0);
      setHedgeHoldSwitchPct(0);
    };

    const handleEntryHoldRatio = (e) => {
      const newEntryHoldRatio = e.target.value.trim();
      setEntryHoldRatio(newEntryHoldRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryHoldRatio)) {
          setEntryHoldRatioValid(true);
          }
        const num = parseFloat(newEntryHoldRatio);
        if (newEntryHoldRatio === '' || !Number.isFinite(num) || num <= 0) {
          resetEntryHoldPreserveUiDefaults();
        }
        };
    
    const handleHedgeHoldRatio = (e) => {
      const newHedgeHoldRatio = e.target.value.trim();
      setHedgeHoldRatio(newHedgeHoldRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newHedgeHoldRatio)) {
          setHedgeHoldRatioValid(true);
          }
        const num = parseFloat(newHedgeHoldRatio);
        if (newHedgeHoldRatio === '' || !Number.isFinite(num) || num <= 0) {
          resetHedgeHoldPreserveUiDefaults();
        }
        };
        
    const handleAddSymbol = (symbol) => {
      const confirmed = window.confirm(`${symbol}을 리스트에 추가할까요?`);
      if (confirmed) {
      fetchBand(`/add_user_symbols_band_front?symbol=${symbol}`)
      .then(res => res.json())
      .then(data => {
        if (data.msg) {
            alert(data.msg); // msg가 존재할 때만 출력
            return;
          }
        setSymbolList(data.signal)
      })
      .catch(err => console.error(err));
      }
    };

    const deleteSymbol = (symbol) => {
      if (symbol === "default") {
        alert('코인을 먼저 선택하세요!');
        return; // Stop execution if the condition is met
      }
      const confirmed = window.confirm(`${symbol}이 포지션이 걸려 있다면 리스트에서 삭제 후 수동으로 포지션 정리해주세요! ${symbol}을 삭제하시겠습니까?`);
      if (confirmed) {
      fetchBand(
        `/delete_user_symbols_band?symbol=${encodeURIComponent(symbol)}&both_mode=${encodeURIComponent(both_mode)}`
      )
      .then(res => res.json())
      .then(data => setSymbolList(data.signal))
      .catch(err => console.error(err));
      removeListItem(symbol, setE_Items);
      removeListItem(symbol, setW_Items);
      setSymbol('default');
      clearBandHedgeHlDisplay();
      }
    };

    const handleOpenPopup = (event) => {
      if (mode === 'auto') {
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const popupWidth = 300;
      const popupHeight = 320;

      const desiredTop = rect.top + scrollTop - popupHeight - 16;
      const top = Math.max(scrollTop + 20, desiredTop);
      const minLeft = scrollLeft + 16;
      const maxLeft = scrollLeft + viewportWidth - popupWidth - 16;
      const candidateLeft = rect.left + scrollLeft;
      const left = Math.min(Math.max(candidateLeft, minLeft), maxLeft);

      setPopupAnchor({ top, left });
      setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
      setIsPopupOpen(false);
      setPopupAnchor(null);
    };        

    const addListItem = (newItem, listSetter) => {
      listSetter(prevList => [...prevList, newItem]);
    };

    const removeListItem = (itemName, listSetter) => {
      listSetter(prevList => prevList.filter(item => item !== itemName));
    };
    
    const onClickCoin = (coin) => {
      setSymbol(coin);
      onClickSearch(coin);
    }

    useEffect(() => {

      if(symbolCountValid && fPercentLowValid && fPercentHighValid && fluctuationValid && leverageValid && emaStandardValid &&
        sensitivityValid && atrperiodValid && sensitivityStandardValid && atrperiodStandardValid &&
        emaStandard2ndValid && sensitivityStandard2ndValid && atrperiodStandard2ndValid && sensitivityCloseValid &&
        atrperiodCloseValid && sensitivityCloseShortValid && atrperiodCloseShortValid &&
        sensitivityCircleEntryValid && atrperiodCircleEntryValid && sensitivityCircleEntryShortValid && atrperiodCircleEntryShortValid &&
        sensitivityCircleValid && atrperiodCircleValid && sensitivityCircleShortValid && atrperiodCircleShortValid &&
        benefit_ratioratioValid && entryusdtValid_1 && circle_rateValid && circle_rate_intervalValid && trigger_ratioValid && double_benefitValid && total_benefit_usdtValid && total_benefit_countValid && welfare_ratioValid && start_ratioValid && confirmed_ratioValid && max_ratioValid && bandMaxLosscutPctValid &&
        entry_hold_ratioValid && hedge_hold_ratioValid
        ) {
        setNotAllow(false);
        return;
      }
      setNotAllow(true);
    }, [symbolCountValid, fPercentLowValid, fPercentHighValid, fluctuationValid, leverageValid, emaStandardValid,
       sensitivityValid, atrperiodValid, sensitivityStandardValid, atrperiodStandardValid,
       emaStandard2ndValid, sensitivityStandard2ndValid, atrperiodStandard2ndValid, sensitivityCloseValid,
       atrperiodCloseValid, sensitivityCloseShortValid, atrperiodCloseShortValid,
       sensitivityCircleEntryValid, atrperiodCircleEntryValid, sensitivityCircleEntryShortValid, atrperiodCircleEntryShortValid,
       sensitivityCircleValid, atrperiodCircleValid, sensitivityCircleShortValid, atrperiodCircleShortValid,
       benefit_ratioratioValid, entryusdtValid_1, circle_rateValid, circle_rate_intervalValid, trigger_ratioValid, double_benefitValid, total_benefit_usdtValid, total_benefit_countValid, welfare_ratioValid, start_ratioValid, confirmed_ratioValid, max_ratioValid, bandMaxLosscutPctValid,
       entry_hold_ratioValid, hedge_hold_ratioValid
      ]);

    useEffect(() => {
        fetchBand(`/get_user_symbols_band`)
        .then(res => res.json())
        .then(data => setSymbolList(data.signal))
        .catch(err => console.error(err));
    },[symbol,targetUid]);

    useEffect(() => {
      if (didInitSearch.current) return;
      if (!targetUid && !uid) return;
      if (setlevel === 0 || setlevel === '0') return;
      didInitSearch.current = true;
      onClickSearch('default');
    }, [targetUid, uid, setlevel]);

    useEffect(() => {
      if (!welfareRatioChangedByUser.current) return; // 사용자 입력이 아닌 경우 스킵
      if (main_balance === 0 || second_balance === 0) {
        alert('메인 잔고 또는 세컨드 잔고가 0원입니다. 잔고를 확인하고 진행하세요!');
        return;
      }
      const ratio = parseFloat(welfare_ratio);
      setEntryUsdt_1((main_balance + second_balance) * ratio / 100);
    }, [welfare_ratio, main_balance, second_balance]);

    const onClickSamaria_check = () => {
      window.open('/SamariaSignals', '_blank');
      return;
    }

    const onClickSearch_auto = () => {
      fetchBand(`/setdata_first_band`)
      .then((response) => {
        if(!response.ok) {
          throw new Error('400 아니면 500 에러남');
        }
        return response.json()
      })
      .then((result) => {
        if (!result || result.length === 0) {
        console.log("데이터가 비어있음, 그냥 넘어감");
        return; // 아무것도 안 함
      }

        const data = result[0]; // 안전하게 변수로 받음
        console.log('db에서 성공적으로 데이터 받아옴');

        setFullMode(data.full_mode);
        setEntryBasis(data.entry_basis === 'turtle' ? 'turtle' : 'kinpoint');
        applyBandHedgeHlFromRow(data);
        applyBandCloseHlFromRow(data);
        setCloseMode(data.close_mode === 'extreme_hl' ? 'extreme_hl' : 'kinpoint');
        setBothMode(data.both_mode);
        setSymbolCount(data.auto_count);
        setTopBottom(data.top_bottom);
        setFluctuation(data.fluctuation)
        setFundingRate(data.funding_rate);
        setFpercentLow(data.f_percent_low);
        setFpercentHigh(data.f_percent_high);

        setHedgeMode(data.hedge_mode);
        setHedgeOutChecking(data.hedge_out_checking);
        setHedgeOutCandle(data.hedge_out_candle || data.circle_entry_candle);
        setLeverage(data.leverage_info);
        setEntryCandle(data.entry_candle);
        setHedgeCandle(data.entry_candle || data.hedge_candle);
        setEntryExtremeAvgMode(
          data.entry_extreme_avg_mode === 'apply' ? 'apply' : 'disable'
        );
        setBandDcaMode(resolveBandDcaModeForHedge(data.hedge_mode, data.band_dca_mode));
        setBandDcaSteps(parseBandDcaSteps(data.band_dca_steps));
        setCloseCandle(data.close_candle);

        setEntryChecking(data.entry_checking);
        setStandardChecking(data.standard_checking);
        setCloseChecking(data.close_checking);

        setStartRatio(data.start_ratio);
        setConfirmedRatio(data.confirmed_ratio);
        setMaxRatio(data.max_ratio);

        setSensitivity(data.sensitivity);
        setAtrPeriod(data.atr_period);
        setSensitivityShort(data.sensitivity_short);
        setAtrPeriodShort(data.atr_period_short);

        setEntryStandard(data.entry_standard);
        setEmaStandard(result[0].ema_standard);
        setEmaLosscut(result[0].ema_losscut);
        setEmaHedgeGapRatio(result[0].ema_hedge_gap_ratio != null ? Number(result[0].ema_hedge_gap_ratio) : 0);
        setCandleStandard(data.candle_standard);
        setCandleTypeStandard(data.candle_type_standard);
        setSensitivityStandard(data.sensitivity_standard);
        setAtrPeriodStandard(data.atr_period_standard);

        setEntryStandard2nd(data.entry_standard_2nd != null ? data.entry_standard_2nd : 'disable');
        setEmaStandard2nd(data.ema_standard_2nd != null ? data.ema_standard_2nd : 200);
        setCandleStandard2nd(data.candle_standard_2nd != null ? data.candle_standard_2nd : '4h');
        setCandleTypeStandard2nd(data.candle_type_standard_2nd != null ? data.candle_type_standard_2nd : 'haikin');
        setSensitivityStandard2nd(data.sensitivity_standard_2nd != null ? data.sensitivity_standard_2nd : 2);
        setAtrPeriodStandard2nd(data.atr_period_standard_2nd != null ? data.atr_period_standard_2nd : 2);

        setHedgeCandle(data.entry_candle || data.hedge_candle);
        setHedgeChecking(data.hedge_checking);
        setBandMaxLosscutPct(data.band_max_losscut_pct != null ? Number(data.band_max_losscut_pct) : 0);
        setBandMaxLosscutPctValid(true);

        setCircleEntryCandle(data.circle_entry_candle);
        setSensitivityCircleEntry(data.sensitivity_circle_entry);
        setAtrPeriodCircleEntry(data.atr_period_circle_entry);
        setSensitivityCircleEntryShort(data.sensitivity_circle_entry_short);
        setAtrPeriodCircleEntryShort(data.atr_period_circle_entry_short);
        setCandleTypeCircleEntry(data.candle_type_circle_entry);
        setCircleEntryChecking(data.circle_entry_checking);
        setCircleEntryMode(data.circle_entry_mode);

        setCircleHedgeChecking(data.circle_hedge_checking);
        setCircleHedgeMode(data.circle_hedge_mode);
        setCircleHedgeCandle(data.circle_hedge_candle || data.circle_entry_candle);

        setCircleCandle(data.circle_candle);
        setSensitivityCircle(data.sensitivity_circle);
        setAtrPeriodCircle(data.atr_period_circle);
        setSensitivityCircleShort(data.sensitivity_circle_short);
        setAtrPeriodCircleShort(data.atr_period_circle_short);
        setCandleTypeCircle(data.candle_type_circle);
        setCircleChecking(data.circle_checking);
        setCircleCloseMode(data.circle_close_mode);

        setSensitivityClose(data.sensitivity_close);
        setAtrPeriodClose(data.atr_period_close);
        setSensitivityCloseShort(data.sensitivity_close_short);
        setAtrPeriodCloseShort(data.atr_period_close_short);

        setDirection(data.direction);

        setBenefitRatio(data.benefit_ratio);

        setTriggerMode(data.trigger_mode);
        setTriggerRatio(data.trigger_ratio);

        setWelfareMode(data.welfare_mode);
        setWelfareRatio(data.welfare_ratio);
        setEntryUsdt_1(data.entry_usdt_1);

        setCircleMode(data.circle_mode);
        setCircleRate(data.circle_rate);
        setCircleRateInterval(data.circle_rate_interval);
        setCircleBenefitMode(data.circle_benefit_mode);

        setDoubleBenefit(data.double_benefit);
        setDoubleContinue(data.double_continue);

        setCandleTypeEntry(data.candle_type_entry);
        setCandleTypeClose(data.candle_type_close);

        setEntryHoldRatio(data.entry_hold_ratio);
        if (Number(data.entry_hold_ratio) <= 0) {
          setEntryHoldMode('disable');
          setEntryHoldRoiPct(0);
          setEntryHoldSwitchPct(0);
        } else {
          setEntryHoldMode(data.entry_hold_mode);
          setEntryHoldRoiPct(
            data.entry_hold_roi_pct != null && Number(data.entry_hold_roi_pct) > 0
              ? Number(data.entry_hold_roi_pct)
              : 0,
          );
          setEntryHoldSwitchPct(
            data.entry_hold_switch_pct != null && Number(data.entry_hold_switch_pct) > 0
              ? Number(data.entry_hold_switch_pct)
              : 0,
          );
        }
        setHedgeHoldRatio(data.hedge_hold_ratio);
        if (Number(data.hedge_hold_ratio) <= 0) {
          setHedgeHoldMode('disable');
          setHedgeHoldRoiPct(0);
          setHedgeHoldSwitchPct(0);
        } else {
          setHedgeHoldMode(data.hedge_hold_mode);
          setHedgeHoldRoiPct(
            data.hedge_hold_roi_pct != null && Number(data.hedge_hold_roi_pct) > 0
              ? Number(data.hedge_hold_roi_pct)
              : 0,
          );
          setHedgeHoldSwitchPct(
            data.hedge_hold_switch_pct != null && Number(data.hedge_hold_switch_pct) > 0
              ? Number(data.hedge_hold_switch_pct)
              : 0,
          );
        }

      })
      .catch((error) => {
        console.error('에러남:', error.message || error);
      })
    }

    const onClickSearch = (symbol) => {
      let urlName
      const str = symbol;
      console.log(str);
      if(str === 'default'){
        urlName = 'default'
      }else{
        const index = str.indexOf('/');
        urlName = str.substring(0,index);
      }  
      fetchBand(`/setdata_band/${urlName}/${setlevel}`)
      .then((response) => {
        if(!response.ok) {
          throw new Error('400 아니면 500 에러남');
        }
        return response.json()
      })
      .then((result) => {
        const data = result[0]; // 안전하게 변수로 받음
        console.log('db에서 성공적으로 데이터 받아옴');
        setFullMode(data.full_mode);
        setEntryBasis(data.entry_basis === 'turtle' ? 'turtle' : 'kinpoint');
        if (str === 'default') {
          clearBandHedgeHlDisplay();
        } else {
          applyBandHedgeHlFromRow(data);
        applyBandCloseHlFromRow(data);
        setCloseMode(data.close_mode === 'extreme_hl' ? 'extreme_hl' : 'kinpoint');
        }
        setBothMode(data.both_mode);
        setHedgeMode(data.hedge_mode);
        setHedgeOutChecking(data.hedge_out_checking);
        setHedgeOutCandle(data.hedge_out_candle || data.circle_entry_candle);
        setLeverage(data.leverage_info);
        setEntryCandle(data.entry_candle);
        setHedgeCandle(data.entry_candle || data.hedge_candle);
        setEntryExtremeAvgMode(
          data.entry_extreme_avg_mode === 'apply' ? 'apply' : 'disable'
        );
        setBandDcaMode(resolveBandDcaModeForHedge(data.hedge_mode, data.band_dca_mode));
        setBandDcaSteps(parseBandDcaSteps(data.band_dca_steps));
        setCloseCandle(data.close_candle);

        setEntryChecking(data.entry_checking);
        setStandardChecking(data.standard_checking);
        setCloseChecking(data.close_checking);

        setStartRatio(data.start_ratio);
        setConfirmedRatio(data.confirmed_ratio);
        setMaxRatio(data.max_ratio);

        setSensitivity(data.sensitivity);
        setAtrPeriod(data.atr_period);
        setSensitivityShort(data.sensitivity_short);
        setAtrPeriodShort(data.atr_period_short);

        setEntryStandard(data.entry_standard);
        setEmaStandard(result[0].ema_standard);
        setEmaLosscut(result[0].ema_losscut);
        setEmaHedgeGapRatio(result[0].ema_hedge_gap_ratio != null ? Number(result[0].ema_hedge_gap_ratio) : 0);
        setCandleStandard(data.candle_standard);
        setCandleTypeStandard(data.candle_type_standard);
        setSensitivityStandard(data.sensitivity_standard);
        setAtrPeriodStandard(data.atr_period_standard);

        setEntryStandard2nd(data.entry_standard_2nd != null ? data.entry_standard_2nd : 'disable');
        setEmaStandard2nd(data.ema_standard_2nd != null ? data.ema_standard_2nd : 200);
        setCandleStandard2nd(data.candle_standard_2nd != null ? data.candle_standard_2nd : '4h');
        setCandleTypeStandard2nd(data.candle_type_standard_2nd != null ? data.candle_type_standard_2nd : 'haikin');
        setSensitivityStandard2nd(data.sensitivity_standard_2nd != null ? data.sensitivity_standard_2nd : 2);
        setAtrPeriodStandard2nd(data.atr_period_standard_2nd != null ? data.atr_period_standard_2nd : 2);

        setHedgeCandle(data.entry_candle || data.hedge_candle);
        setHedgeChecking(data.hedge_checking);
        setBandMaxLosscutPct(data.band_max_losscut_pct != null ? Number(data.band_max_losscut_pct) : 0);
        setBandMaxLosscutPctValid(true);

        setCircleEntryCandle(data.circle_entry_candle);
        setSensitivityCircleEntry(data.sensitivity_circle_entry);
        setAtrPeriodCircleEntry(data.atr_period_circle_entry);
        setSensitivityCircleEntryShort(data.sensitivity_circle_entry_short);
        setAtrPeriodCircleEntryShort(data.atr_period_circle_entry_short);
        setCandleTypeCircleEntry(data.candle_type_circle_entry);
        setCircleEntryChecking(data.circle_entry_checking);
        setCircleEntryMode(data.circle_entry_mode);

        setCircleHedgeChecking(data.circle_hedge_checking);
        setCircleHedgeMode(data.circle_hedge_mode);
        setCircleHedgeCandle(data.circle_hedge_candle || data.circle_entry_candle);

        setCircleCandle(data.circle_candle);
        setSensitivityCircle(data.sensitivity_circle);
        setAtrPeriodCircle(data.atr_period_circle);
        setSensitivityCircleShort(data.sensitivity_circle_short);
        setAtrPeriodCircleShort(data.atr_period_circle_short);
        setCandleTypeCircle(data.candle_type_circle);
        setCircleChecking(data.circle_checking);
        setCircleCloseMode(data.circle_close_mode);

        setSensitivityClose(data.sensitivity_close);
        setAtrPeriodClose(data.atr_period_close);
        setSensitivityCloseShort(data.sensitivity_close_short);
        setAtrPeriodCloseShort(data.atr_period_close_short);

        setDirection(data.direction);

        setBenefitRatio(data.benefit_ratio);

        setTriggerMode(data.trigger_mode);
        setTriggerRatio(data.trigger_ratio);

        setWelfareMode(data.welfare_mode);
        setWelfareRatio(data.welfare_ratio);
        setEntryUsdt_1(data.entry_usdt_1);
        
        setCircleMode(data.circle_mode);
        setCircleRate(data.circle_rate);
        setCircleRateInterval(data.circle_rate_interval);
        setCircleBenefitMode(data.circle_benefit_mode);

        setDoubleBenefit(data.double_benefit);
        setDoublePnl(data.double_pnl);
        setDoubleContinue(data.double_continue);

        setTotalBenefitUsdt(data.total_benefit_usdt);
        setTotalBenefitUsdtObj(data.total_benefit_usdt_obj);
        setTotalBenefitCount(data.total_benefit_count);
        setTotalCurrentCount(data.total_current_count);
        applyTotalBenefitStart(data.total_benefit_start);
        setTotalContinueMode(data.total_continue_mode);
        setTotalContinueDirection(data.total_continue_direction);
        setSingleTpContinueMode(data.single_tp_continue_mode || 'disable');

        setCandleTypeEntry(data.candle_type_entry);
        setCandleTypeClose(data.candle_type_close);

        setTradeStart(data.trade_start);
        setBenefit_stop(data.current_symbol_stop);
        setCurrentCoin_stop(data.current_coin_clear);
        setAllCoin_stop(data.all_coin_clear);

        setTotalPnl(data.totalpnl);
        setHedgeContinue(data.hedge_continue);

        setEntryHoldRatio(data.entry_hold_ratio);
        if (Number(data.entry_hold_ratio) <= 0) {
          setEntryHoldMode('disable');
          setEntryHoldRoiPct(0);
          setEntryHoldSwitchPct(0);
        } else {
          setEntryHoldMode(data.entry_hold_mode);
          setEntryHoldRoiPct(
            data.entry_hold_roi_pct != null && Number(data.entry_hold_roi_pct) > 0
              ? Number(data.entry_hold_roi_pct)
              : 0,
          );
          setEntryHoldSwitchPct(
            data.entry_hold_switch_pct != null && Number(data.entry_hold_switch_pct) > 0
              ? Number(data.entry_hold_switch_pct)
              : 0,
          );
        }
        setHedgeHoldRatio(data.hedge_hold_ratio);
        if (Number(data.hedge_hold_ratio) <= 0) {
          setHedgeHoldMode('disable');
          setHedgeHoldRoiPct(0);
          setHedgeHoldSwitchPct(0);
        } else {
          setHedgeHoldMode(data.hedge_hold_mode);
          setHedgeHoldRoiPct(
            data.hedge_hold_roi_pct != null && Number(data.hedge_hold_roi_pct) > 0
              ? Number(data.hedge_hold_roi_pct)
              : 0,
          );
          setHedgeHoldSwitchPct(
            data.hedge_hold_switch_pct != null && Number(data.hedge_hold_switch_pct) > 0
              ? Number(data.hedge_hold_switch_pct)
              : 0,
          );
        }

        const e_checklist = isItemInList(data.symbol, e_items);
        const w_checklist = isItemInList(data.symbol, w_items);
        
        if(str !== 'default'){
          result.forEach(item => {
            // console.log('item',item)
            if ((item.trade_start === 1 || item.trade_start === '1') && e_checklist === false) {
              addListItem(item.symbol, setE_Items);
            }
            if ((item.trade_start === 0 || item.trade_start === '0') && e_checklist === true) {
              removeListItem(item.symbol, setE_Items);
            }
          
            // Waiting coin 리스트 삽입 및 삭제
            if ((item.current_symbol_stop === 1 || item.current_symbol_stop === '1') && w_checklist === false) {
              addListItem(item.symbol, setW_Items);
            }
            if ((item.current_symbol_stop === 0 || item.current_symbol_stop === '0') && w_checklist === true) {
              removeListItem(item.symbol, setW_Items);
            }
          });
        };

        console.log('e_items',e_items);
        console.log('e_checklist',e_checklist);
      })
      .catch((error) => {
        console.log('에러남');
      })
    }

    const onClickAutoStart = () => {

      let confirmed = false

      confirmed = window.confirm('HOLD 버전 자동매매를 시작하시겠습니까?');

      if (confirmed) {
        console.log('스타트 버튼 클릭')
        fetchBand('/start_band_auto', {
          method : 'POST',
          headers: {
            'Content-Type' : 'application/json',
          },
          body: JSON.stringify({
            full_mode,
            both_mode,
            auto_count,
            top_bottom,
            fluctuation,
            funding_rate,
            f_percent_low,
            f_percent_high,
            entry_mode,
            entry_basis,
            hedge_mode,
            hedge_out_checking,
            hedge_out_candle,
            leverage_info,
            sensitivity,
            atr_period,
            sensitivity_short,
            atr_period_short,
            entry_continue: 'disable',
            entry_standard,
            ema_standard,
            ema_losscut,
            ema_hedge_gap_ratio,
            candle_standard,
            candle_type_standard,
            sensitivity_standard,
            atr_period_standard,
            entry_standard_2nd,
            ema_standard_2nd,
            candle_standard_2nd,
            candle_type_standard_2nd,
            sensitivity_standard_2nd,
            atr_period_standard_2nd,
            hedge_candle: entry_candle,
            hedge_checking,
            band_max_losscut_pct: Number(band_max_losscut_pct) || 0,
            circle_entry_candle,
            sensitivity_circle_entry,
            atr_period_circle_entry,
            sensitivity_circle_entry_short,
            atr_period_circle_entry_short,
            candle_type_circle_entry,
            circle_entry_checking,
            circle_entry_mode,
            circle_hedge_checking,
            circle_hedge_mode,
            circle_hedge_candle,
            circle_candle,
            sensitivity_circle,
            atr_period_circle,
            sensitivity_circle_short,
            atr_period_circle_short,
            candle_type_circle,
            circle_checking,
            circle_close_mode,
            sensitivity_close,
            atr_period_close,
            sensitivity_close_short,
            atr_period_close_short,
            entry_candle,
            entry_checking,
            standard_checking,
            close_checking,
            close_mode,
            start_ratio,
            confirmed_ratio,
            max_ratio,
            close_candle,
            direction,
            benefit_ratio: isBandTurtleEntryBasis(entry_basis) ? 0 : benefit_ratio,
            trigger_mode,
            trigger_ratio,
            welfare_mode,
            welfare_ratio,
            entry_usdt_1,
            double_benefit,
            double_continue,
            circle_mode,
            circle_rate,
            circle_rate_interval,
            circle_benefit_mode,
            candle_type_entry,
            candle_type_close,
            entry_hold_ratio,
            entry_hold_mode,
            entry_hold_roi_pct: decimalPctToNumber(entry_hold_roi_pct),
            entry_hold_switch_pct: decimalPctToNumber(entry_hold_switch_pct),
            hedge_hold_ratio,
            hedge_hold_mode,
            hedge_hold_roi_pct: decimalPctToNumber(hedge_hold_roi_pct),
            hedge_hold_switch_pct: decimalPctToNumber(hedge_hold_switch_pct),
            hedge_hl_bar_count: resolveHedgeHlBarCount(),
            close_tp_hl_bar_count: resolveCloseTpHlBarCount(),
            single_tp_continue_mode,
            entry_extreme_avg_mode,
            band_dca_mode: resolveBandDcaModeForHedge(hedge_mode, band_dca_mode),
            band_dca_steps,
            }),
         })
         .then(response => response.json())  // 응답을 JSON 형식으로 변환
         .then(data => {
          alert(data.message);  // JSON에서 message 값을 가져옴
         })
         .catch(error => console.error('Error:', error));
       };

      onClickSearch_auto();
      // 첫 번째 실행 (1초 후)
      setTimeout(() => {
        window.location.reload();
      }, 10000); // 첫 실행까지 1초 딜레이
    };

    const onClickStart = () => {

      if (symbol === "default") {
        alert('코인을 먼저 선택하세요!');
        return; // Stop execution if the condition is met
      }
      
      let confirmed = false

      if (trade_start === 0 || trade_start === "0"){
        confirmed = window.confirm(symbol + '의 매매를 시작하시겠습니까?');
      }else if (trade_start === 1 || trade_start === "1"){
        confirmed = window.confirm(symbol + '의 셋팅값을 변경하시겠습니까?');
      }

      if (confirmed) {
        console.log('스타트 버튼 클릭')
        fetchBand('/start_band', {
          method : 'POST',
          headers: {
            'Content-Type' : 'application/json',
          },
          body: JSON.stringify({
            symbol,
            entry_mode,
            entry_basis,
            hedge_mode,
            hedge_out_checking,
            hedge_out_candle,
            full_mode,
            both_mode,
            leverage_info,
            sensitivity,
            atr_period,
            sensitivity_short,
            atr_period_short,
            entry_continue: 'disable',
            entry_standard,
            ema_standard,
            ema_losscut,
            ema_hedge_gap_ratio,
            candle_standard,
            candle_type_standard,
            sensitivity_standard,
            atr_period_standard,
            entry_standard_2nd,
            ema_standard_2nd,
            candle_standard_2nd,
            candle_type_standard_2nd,
            sensitivity_standard_2nd,
            atr_period_standard_2nd,
            hedge_candle: entry_candle,
            hedge_checking,
            band_max_losscut_pct: Number(band_max_losscut_pct) || 0,
            circle_entry_candle,
            sensitivity_circle_entry,
            atr_period_circle_entry,
            sensitivity_circle_entry_short,
            atr_period_circle_entry_short,
            candle_type_circle_entry,
            circle_entry_checking,
            circle_entry_mode,
            circle_hedge_checking,
            circle_hedge_mode,
            circle_hedge_candle,
            circle_candle,
            sensitivity_circle,
            atr_period_circle,
            sensitivity_circle_short,
            atr_period_circle_short,
            candle_type_circle,
            circle_checking,
            circle_close_mode,
            sensitivity_close,
            atr_period_close,
            sensitivity_close_short,
            atr_period_close_short,
            entry_candle,
            entry_checking,
            standard_checking,
            close_checking,
            close_mode,
            start_ratio,
            confirmed_ratio,
            max_ratio,
            close_candle,
            direction,
            benefit_ratio: isBandTurtleEntryBasis(entry_basis) ? 0 : benefit_ratio,
            trigger_mode,
            trigger_ratio,
            welfare_mode,
            welfare_ratio,
            entry_usdt_1,
            double_benefit,
            double_continue,
            circle_mode,
            circle_rate,
            circle_rate_interval,
            circle_benefit_mode,
            candle_type_entry,
            candle_type_close,
            hedge_continue,
            entry_hold_ratio,
            entry_hold_mode,
            entry_hold_roi_pct: decimalPctToNumber(entry_hold_roi_pct),
            entry_hold_switch_pct: decimalPctToNumber(entry_hold_switch_pct),
            hedge_hold_ratio,
            hedge_hold_mode,
            hedge_hold_roi_pct: decimalPctToNumber(hedge_hold_roi_pct),
            hedge_hold_switch_pct: decimalPctToNumber(hedge_hold_switch_pct),
            hedge_hl_bar_count: resolveHedgeHlBarCount(),
            close_tp_hl_bar_count: resolveCloseTpHlBarCount(),
            single_tp_continue_mode,
            entry_extreme_avg_mode,
            band_dca_mode: resolveBandDcaModeForHedge(hedge_mode, band_dca_mode),
            band_dca_steps,
            }),
         })
         .then(response => response.json())  // 응답을 JSON 형식으로 변환
         .then(data => {
          onClickSearch(symbol);
          alert(data.message);  // JSON에서 message 값을 가져옴
         })
         .catch(error => console.error('Error:', error));
       };
    };

    const buildBandPresetPayload = () => ({
      entry_mode,
      entry_basis,
      hedge_mode,
      hedge_out_checking,
      hedge_out_candle,
      full_mode,
      both_mode,
      leverage_info,
      sensitivity,
      atr_period,
      sensitivity_short,
      atr_period_short,
      entry_continue: 'disable',
      entry_standard,
      ema_standard,
      ema_losscut,
      ema_hedge_gap_ratio,
      candle_standard,
      candle_type_standard,
      sensitivity_standard,
      atr_period_standard,
      entry_standard_2nd,
      ema_standard_2nd,
      candle_standard_2nd,
      candle_type_standard_2nd,
      sensitivity_standard_2nd,
      atr_period_standard_2nd,
      hedge_candle: entry_candle,
      hedge_checking,
      band_max_losscut_pct: Number(band_max_losscut_pct) || 0,
      circle_entry_candle,
      sensitivity_circle_entry,
      atr_period_circle_entry,
      sensitivity_circle_entry_short,
      atr_period_circle_entry_short,
      candle_type_circle_entry,
      circle_entry_checking,
      circle_entry_mode,
      circle_hedge_checking,
      circle_hedge_mode,
      circle_hedge_candle,
      circle_candle,
      sensitivity_circle,
      atr_period_circle,
      sensitivity_circle_short,
      atr_period_circle_short,
      candle_type_circle,
      circle_checking,
      circle_close_mode,
      sensitivity_close,
      atr_period_close,
      sensitivity_close_short,
      atr_period_close_short,
      entry_candle,
      entry_checking,
      standard_checking,
      close_checking,
      close_mode,
      start_ratio,
      confirmed_ratio,
      max_ratio,
      close_candle,
      direction,
      benefit_ratio: isBandTurtleEntryBasis(entry_basis) ? 0 : benefit_ratio,
      trigger_mode,
      trigger_ratio,
      welfare_mode,
      welfare_ratio,
      entry_usdt_1,
      double_benefit,
      double_continue,
      circle_mode,
      circle_rate,
      circle_rate_interval,
      circle_benefit_mode,
      candle_type_entry,
      candle_type_close,
      hedge_continue,
      entry_hold_ratio,
      entry_hold_mode,
      entry_hold_roi_pct: decimalPctToNumber(entry_hold_roi_pct),
      entry_hold_switch_pct: decimalPctToNumber(entry_hold_switch_pct),
      hedge_hold_ratio,
      hedge_hold_mode,
      hedge_hold_roi_pct: decimalPctToNumber(hedge_hold_roi_pct),
      hedge_hold_switch_pct: decimalPctToNumber(hedge_hold_switch_pct),
      hedge_hl_bar_count: resolveHedgeHlBarCount(),
      close_tp_hl_bar_count: resolveCloseTpHlBarCount(),
      single_tp_continue_mode,
      entry_extreme_avg_mode,
      band_dca_mode: resolveBandDcaModeForHedge(hedge_mode, band_dca_mode),
      band_dca_steps,
    });

    const applyBandPreset = (payload = {}) => {
      if (payload.hedge_mode !== undefined) {
        setHedgeMode(payload.hedge_mode);
        if (isBandHedgeGridExhaustMode(payload.hedge_mode)) {
          setBandDcaMode('kinpoint');
        }
      }
      if (payload.hedge_out_checking !== undefined) setHedgeOutChecking(payload.hedge_out_checking);
      if (payload.hedge_out_candle !== undefined) setHedgeOutCandle(payload.hedge_out_candle);
      if (payload.full_mode !== undefined) setFullMode(payload.full_mode);
      if (payload.entry_basis !== undefined) {
        setEntryBasis(payload.entry_basis === 'turtle' ? 'turtle' : 'kinpoint');
      }
      if (payload.hedge_hl_bar_count !== undefined) {
        setHedgeHlBarCount(Math.max(1, parseInt(payload.hedge_hl_bar_count, 10) || BAND_DEFAULT_HEDGE_HL_BAR_COUNT));
      }
      if (payload.both_mode !== undefined) setBothMode(payload.both_mode);
      if (payload.leverage_info !== undefined) setLeverage(payload.leverage_info);
      if (payload.sensitivity !== undefined) setSensitivity(payload.sensitivity);
      if (payload.atr_period !== undefined) setAtrPeriod(payload.atr_period);
      if (payload.sensitivity_short !== undefined) setSensitivityShort(payload.sensitivity_short);
      if (payload.atr_period_short !== undefined) setAtrPeriodShort(payload.atr_period_short);
      if (payload.entry_standard !== undefined) setEntryStandard(payload.entry_standard);
      if (payload.ema_standard !== undefined) setEmaStandard(payload.ema_standard);
      if (payload.ema_losscut !== undefined) setEmaLosscut(payload.ema_losscut);
      if (payload.ema_hedge_gap_ratio !== undefined) setEmaHedgeGapRatio(Number(payload.ema_hedge_gap_ratio) || 0);
      if (payload.candle_standard !== undefined) setCandleStandard(payload.candle_standard);
      if (payload.candle_type_standard !== undefined) setCandleTypeStandard(payload.candle_type_standard);
      if (payload.sensitivity_standard !== undefined) setSensitivityStandard(payload.sensitivity_standard);
      if (payload.atr_period_standard !== undefined) setAtrPeriodStandard(payload.atr_period_standard);
      if (payload.entry_standard_2nd !== undefined) setEntryStandard2nd(payload.entry_standard_2nd);
      if (payload.ema_standard_2nd !== undefined) setEmaStandard2nd(payload.ema_standard_2nd);
      if (payload.candle_standard_2nd !== undefined) setCandleStandard2nd(payload.candle_standard_2nd);
      if (payload.candle_type_standard_2nd !== undefined) setCandleTypeStandard2nd(payload.candle_type_standard_2nd);
      if (payload.sensitivity_standard_2nd !== undefined) setSensitivityStandard2nd(payload.sensitivity_standard_2nd);
      if (payload.atr_period_standard_2nd !== undefined) setAtrPeriodStandard2nd(payload.atr_period_standard_2nd);
      if (payload.hedge_candle !== undefined) setHedgeCandle(payload.hedge_candle);
      if (payload.hedge_checking !== undefined) setHedgeChecking(payload.hedge_checking);
      if (payload.band_max_losscut_pct !== undefined) {
        setBandMaxLosscutPct(Number(payload.band_max_losscut_pct) || 0);
        setBandMaxLosscutPctValid(true);
      }
      if (payload.circle_entry_candle !== undefined) setCircleEntryCandle(payload.circle_entry_candle);
      if (payload.sensitivity_circle_entry !== undefined) setSensitivityCircleEntry(payload.sensitivity_circle_entry);
      if (payload.atr_period_circle_entry !== undefined) setAtrPeriodCircleEntry(payload.atr_period_circle_entry);
      if (payload.sensitivity_circle_entry_short !== undefined) setSensitivityCircleEntryShort(payload.sensitivity_circle_entry_short);
      if (payload.atr_period_circle_entry_short !== undefined) setAtrPeriodCircleEntryShort(payload.atr_period_circle_entry_short);
      if (payload.candle_type_circle_entry !== undefined) setCandleTypeCircleEntry(payload.candle_type_circle_entry);
      if (payload.circle_entry_checking !== undefined) setCircleEntryChecking(payload.circle_entry_checking);
      if (payload.circle_entry_mode !== undefined) setCircleEntryMode(payload.circle_entry_mode);
      if (payload.circle_hedge_checking !== undefined) setCircleHedgeChecking(payload.circle_hedge_checking);
      if (payload.circle_hedge_mode !== undefined) setCircleHedgeMode(payload.circle_hedge_mode);
      if (payload.circle_hedge_candle !== undefined) setCircleHedgeCandle(payload.circle_hedge_candle);
      if (payload.circle_candle !== undefined) setCircleCandle(payload.circle_candle);
      if (payload.sensitivity_circle !== undefined) setSensitivityCircle(payload.sensitivity_circle);
      if (payload.atr_period_circle !== undefined) setAtrPeriodCircle(payload.atr_period_circle);
      if (payload.sensitivity_circle_short !== undefined) setSensitivityCircleShort(payload.sensitivity_circle_short);
      if (payload.atr_period_circle_short !== undefined) setAtrPeriodCircleShort(payload.atr_period_circle_short);
      if (payload.candle_type_circle !== undefined) setCandleTypeCircle(payload.candle_type_circle);
      if (payload.circle_checking !== undefined) setCircleChecking(payload.circle_checking);
      if (payload.circle_close_mode !== undefined) setCircleCloseMode(payload.circle_close_mode);
      if (payload.sensitivity_close !== undefined) setSensitivityClose(payload.sensitivity_close);
      if (payload.atr_period_close !== undefined) setAtrPeriodClose(payload.atr_period_close);
      if (payload.sensitivity_close_short !== undefined) setSensitivityCloseShort(payload.sensitivity_close_short);
      if (payload.atr_period_close_short !== undefined) setAtrPeriodCloseShort(payload.atr_period_close_short);
      if (payload.entry_candle !== undefined) {
        setEntryCandle(payload.entry_candle);
        setHedgeCandle(payload.entry_candle);
      }
      if (payload.entry_checking !== undefined) setEntryChecking(payload.entry_checking);
      if (payload.standard_checking !== undefined) setStandardChecking(payload.standard_checking);
      if (payload.close_mode !== undefined) {
        setCloseMode(payload.close_mode === 'extreme_hl' ? 'extreme_hl' : 'kinpoint');
      }
      if (payload.close_tp_hl_bar_count !== undefined) {
        setCloseTpHlBarCount(Math.max(1, parseInt(payload.close_tp_hl_bar_count, 10) || BAND_DEFAULT_CLOSE_TP_HL_BAR_COUNT));
      }
      if (payload.close_hl_high_px !== undefined) setCloseHlHighPx(formatBandRefPx(payload.close_hl_high_px));
      if (payload.close_hl_low_px !== undefined) setCloseHlLowPx(formatBandRefPx(payload.close_hl_low_px));
      if (payload.close_checking !== undefined) setCloseChecking(payload.close_checking);
      if (payload.start_ratio !== undefined) setStartRatio(payload.start_ratio);
      if (payload.confirmed_ratio !== undefined) setConfirmedRatio(payload.confirmed_ratio);
      if (payload.max_ratio !== undefined) setMaxRatio(payload.max_ratio);
      if (payload.close_candle !== undefined) setCloseCandle(payload.close_candle);
      if (payload.direction !== undefined) setDirection(payload.direction);
      if (payload.benefit_ratio !== undefined) setBenefitRatio(payload.benefit_ratio);
      if (payload.trigger_mode !== undefined) setTriggerMode(payload.trigger_mode);
      if (payload.trigger_ratio !== undefined) setTriggerRatio(payload.trigger_ratio);
      if (payload.welfare_mode !== undefined) setWelfareMode(payload.welfare_mode);
      if (payload.welfare_ratio !== undefined) setWelfareRatio(payload.welfare_ratio);
      if (payload.entry_usdt_1 !== undefined) setEntryUsdt_1(payload.entry_usdt_1);
      if (payload.double_benefit !== undefined) setDoubleBenefit(payload.double_benefit);
      if (payload.double_continue !== undefined) setDoubleContinue(payload.double_continue);
      if (payload.circle_mode !== undefined) setCircleMode(payload.circle_mode);
      if (payload.circle_rate !== undefined) setCircleRate(payload.circle_rate);
      if (payload.circle_rate_interval !== undefined) setCircleRateInterval(payload.circle_rate_interval);
      if (payload.circle_benefit_mode !== undefined) setCircleBenefitMode(payload.circle_benefit_mode);
      if (payload.candle_type_entry !== undefined) setCandleTypeEntry(payload.candle_type_entry);
      if (payload.candle_type_close !== undefined) setCandleTypeClose(payload.candle_type_close);
      if (payload.hedge_continue !== undefined) setHedgeContinue(payload.hedge_continue);
      if (payload.entry_hold_ratio !== undefined) setEntryHoldRatio(payload.entry_hold_ratio);
      if (payload.entry_hold_mode !== undefined) setEntryHoldMode(payload.entry_hold_mode);
      if (payload.entry_hold_roi_pct !== undefined) {
        setEntryHoldRoiPct(
          payload.entry_hold_roi_pct != null && Number(payload.entry_hold_roi_pct) > 0
            ? Number(payload.entry_hold_roi_pct)
            : 0
        );
      }
      if (payload.entry_hold_switch_pct !== undefined) {
        setEntryHoldSwitchPct(
          payload.entry_hold_switch_pct != null && Number(payload.entry_hold_switch_pct) > 0
            ? Number(payload.entry_hold_switch_pct)
            : 0,
        );
      }
      if (payload.hedge_hold_ratio !== undefined) setHedgeHoldRatio(payload.hedge_hold_ratio);
      if (payload.hedge_hold_mode !== undefined) setHedgeHoldMode(payload.hedge_hold_mode);
      if (payload.hedge_hold_roi_pct !== undefined) {
        setHedgeHoldRoiPct(
          payload.hedge_hold_roi_pct != null && Number(payload.hedge_hold_roi_pct) > 0
            ? Number(payload.hedge_hold_roi_pct)
            : 0,
        );
      }
      if (payload.hedge_hold_switch_pct !== undefined) {
        setHedgeHoldSwitchPct(
          payload.hedge_hold_switch_pct != null && Number(payload.hedge_hold_switch_pct) > 0
            ? Number(payload.hedge_hold_switch_pct)
            : 0,
        );
      }
      if (payload.single_tp_continue_mode !== undefined) setSingleTpContinueMode(payload.single_tp_continue_mode);
      if (payload.entry_extreme_avg_mode !== undefined) {
        setEntryExtremeAvgMode(payload.entry_extreme_avg_mode === 'apply' ? 'apply' : 'disable');
      }
      if (payload.band_dca_mode !== undefined) {
        setBandDcaMode(
          resolveBandDcaModeForHedge(payload.hedge_mode ?? hedge_mode, payload.band_dca_mode)
        );
      }
      if (payload.band_dca_steps !== undefined) {
        setBandDcaSteps(parseBandDcaSteps(payload.band_dca_steps));
      }
      if (payload.hedge_hl_high_px !== undefined) setHedgeHlHighPx(formatBandRefPx(payload.hedge_hl_high_px));
      if (payload.hedge_hl_low_px !== undefined) setHedgeHlLowPx(formatBandRefPx(payload.hedge_hl_low_px));
      if (payload.band_hl_long_trigger_px !== undefined) {
        setBandHlLongTriggerPx(formatBandRefPx(payload.band_hl_long_trigger_px));
      }
      if (payload.band_hl_short_trigger_px !== undefined) {
        setBandHlShortTriggerPx(formatBandRefPx(payload.band_hl_short_trigger_px));
      }
    };

  
    const onClickStop = () => {

      let confirmed = false

      confirmed = window.confirm('HOLD버전 자동매매를 종료하시겠습니까? 종료하시면 프로그램 자체가 종료되므로 수동으로 포지션 정리하셔야 합니다.');

      if (confirmed) {
        console.log('종료 버튼 클릭')
        fetchBand('/stop_band_auto', {
          method : 'POST',
          headers: {
            'Content-Type' : 'application/json',
          },
          body: JSON.stringify({
            both_mode
            }),
         })
         .then(response => response.json())  // 응답을 JSON 형식으로 변환
         .then(data => {
          alert(data.message);  // JSON에서 message 값을 가져옴
         })
         .catch(error => console.error('Error:', error));
       };

      onClickSearch_auto();
      setTimeout(() => {
            window.location.reload();
          }, 10000); // 첫 실행까지 1초 딜레이
    };

    const onClickMainBalance = (symbol) => {
      const confirmed = window.confirm('메인 잔고를 조회할까요?');
      if (confirmed) {
        fetchBand(`/getdata_main_balance`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('main_balance에서 받아온 데이터');
          setMainBalance(result.main_balance);
        })
        .catch((error) => {
          console.log('에러남');
        })
      }
    }

    const onClickSecondBalance = (symbol) => {
      const confirmed = window.confirm('2nd 잔고를 조회할까요?');
      if (confirmed) {
        fetchBand(`/getdata_second_balance`)
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            alert(data.message || '잔고 조회 오류가 발생했습니다.');
            throw new Error('400 아니면 500 에러남');
          }
          return data;
        })
        .then((result) => {
          console.log('second_balance에서 받아온 데이터');
          setSecondBalance(result.second_balance);
        })
        .catch((error) => {
          console.log('에러남');
        })
      }
    }

    const onClickDoublePnl = (symbol) => {
      const confirmed = window.confirm('현재 두 계정의 합산 코인 수익을 조회할까요?');
      if (confirmed) {
        fetchBand(`/getdata_double_pnl_band`)
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            alert(data.message || '잔고 조회 오류가 발생했습니다.');
            throw new Error('400 아니면 500 에러남');
          }
          return data;
        })
        .then((result) => {
          console.log('current_balance에서 받아온 데이터');
          setDoublePnl(result.double_pnl);
        })
        .catch((error) => {
          console.log('에러남');
        })
      }
    }

    // 종합 익절 검색
    const onClickTotal_search = () => {

      const benefitValue = parseFloat(total_benefit_usdt);

      if (isNaN(benefitValue) || benefitValue === 0) {
        window.alert('종합 익절 값을 넣어주세요!');
        return;
      }

      if (isNaN(main_balance) || main_balance === 0) {
        window.alert('메인 잔고를 먼저 조회해주세요!');
        return;
      }

      if (
          both_mode === 'double' &&
          (isNaN(second_balance) || second_balance === 0)
        ) {
          window.alert(
            '2nd 잔고가 조회되지 않았습니다.\n더블모드 적용 시 2nd 잔고를 조회해주세요!'
          );
          return;
        }

      // ✅ 여기까지 왔다는 것은 모든 조건 통과
      const total_sum = main_balance + second_balance;
      const total_balance = total_sum + (total_sum * benefitValue / 100);

      setTotalBenefitUsdtObj(total_balance);
    };

    // 종합 익절 시작
    const onClickTotal_start = (symbol) => {
      let confirmed

      const benefitValue = parseFloat(total_benefit_usdt);

      if (isNaN(benefitValue) || benefitValue === 0) {
        confirmed = window.confirm('종합 익절 값을 넣어주세요!');
      } else if (isNaN(main_balance) || main_balance === 0) {
        confirmed = window.confirm('메인 잔고를 먼저 조회해주세요!');
      } 
      else {
        if (isNaN(second_balance) || second_balance === 0) {
          confirmed = window.confirm('2nd 잔고가 조회되지 않았습니다. 더블모드 적용시 2nd 잔고를 조회해주세요!');
        }
        confirmed = window.confirm('종합 익절을 시작할까요? 혹시라도 추가 입금시에는 종합익절을 취소한 뒤 진행하세요');
      }

      if (confirmed) {
        const total_balance = main_balance + second_balance;
        fetchBand(`/setdata_total_benefit_band/${benefitValue}/${total_balance}/${total_benefit_count}/${total_continue_mode}/${total_continue_direction}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('total_benefit에서 받아온 데이터');
          // 🔹 서버에서 보내준 메시지를 사용자에게 보여주기
          if (result.message) {
            alert(result.message);
          }
          if (result?.message?.includes('시작')) {
            setTotalBenefitStart(1);
          }
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
          })
        };
    }

    // 종합 익절 취소 
    const onClickTotal_cancel = (symbol) => {
      const confirmed = window.confirm('종합 익절을 취소할까요?');

      if (confirmed) {
        fetchBand(`/setdata_total_benefit_cancel_band`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('total_benefit_cancel에서 받아온 데이터');
          // 🔹 서버에서 보내준 메시지를 사용자에게 보여주기
          if (result.message) {
            alert(result.message);
          }
          setTotalBenefitStart(0);
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    // 익절 후 정리
    const onClickBenefit_stop = (symbol) => {
      const confirmed = window.confirm('Do you want to clean up the selected coin after profiting?');

      if (confirmed) {
        setBenefit_stop(1);
        let urlName
        const str = symbol;
        console.log(str);
        if(str === 'default'){
          urlName = 'default'
        }else{
          const index = str.indexOf('/');
          urlName = str.substring(0,index);
        }
        fetchBand(`/setdata_benefit_band/${urlName}/${setlevel}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('benefit에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
          })
        };
    }

    // 익절 후 정리 취소 
    const onClickBenefit_cancel = (symbol) => {
      const confirmed = window.confirm('Do you want to cancel clearing after profiting the selected coins?');

      if (confirmed) {
        setBenefit_cancel(1);
        let urlName
        const str = symbol;
        console.log(str);
        if(str === 'default'){
          urlName = 'default'
        }else{
          const index = str.indexOf('/');
          urlName = str.substring(0,index);
        }
        fetchBand(`/setdata_benefit_cancel_band/${urlName}/${setlevel}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('benefit_cancel에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    // 현재 코인 정리 
    const onClickCurrentCoin_stop = (symbol) => {
      const confirmed = window.confirm('현재 선택하신 코인의 포지션을 정리하시길 원하십니까? 포지션 정리시 약 5~10초 정도 지연될 수도 있습니다.');

      if (confirmed) {
        setCurrentCoin_stop(true);
        const str = symbol;
        console.log(str);
        const index = str.indexOf('/');
        const urlName = str.substring(0,index);
        console.log(both_mode);
        fetchBand(`/setdata_current_coin_clear_band/${urlName}/${both_mode}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('current_stop에서 받아온 데이터');
          setTimeout(() => {
            window.location.reload();
          }, 2000); // 첫 실행까지 1초 딜레이
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    const getUuidFromBrowser = () => {
      const cookieUuid = cookies.uuid;
      if (cookieUuid) {
        return cookieUuid;
      }

      const fallbackCookie = document.cookie
        .split('; ')
        .find((item) => item.startsWith('uuid='));

      if (!fallbackCookie) {
        return '';
      }

      return decodeURIComponent(fallbackCookie.split('=').slice(1).join('='));
    };

    //로그아웃
    const onClickEnd = () => {
        const uuid = getUuidFromBrowser();
        if (!uuid) {
          alert('로그인 정보가 존재하지 않습니다.');
          return;
        }
        const logoutUrl = isAdminAccess ? '/super_logout' : '/logout';
        const redirectUrl = isAdminAccess ? '/AdminLogin' : '/';

        fetch(logoutUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userId: uuid })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('로그아웃 요청 실패');
          }
          console.log('로그아웃 성공');
          alert('Logout is successful.');
          window.location.href = redirectUrl;
        })
        .catch(error => {
          console.error('로그아웃 요청 중 오류 발생:', error);
        });
      // UUID를 서버로 보내는 예시 코드
    }

    const isTotalBenefitRunning = Number(total_benefit_start) === 1;

    const bandDcaStepColumns = useMemo(() => {
      const rows = band_dca_steps.map((step, idx) => ({
        label: `${idx + 1}단`,
        originalIdx: idx,
        usdt: step.usdt,
        gap_pct: step.gap_pct,
      }));
      const colSize = Math.ceil(rows.length / 2);
      return [rows.slice(0, colSize), rows.slice(colSize)];
    }, [band_dca_steps]);

    return (
      <main className="kp-console kp-console--hold">
        <div className="kp-console__glare" aria-hidden="true" />
        <section className="kp-console__panel">
          <header className="kp-hero">
            <p className="kp-hero__eyebrow">Bitgenstone auto trade system</p>
            <h1 className="kp-hero__title">BAND SET cockpit</h1>
            <p className="kp-hero__subtitle">
              BAND SET 전략의 자동 셋팅을 한 화면에서 제어하세요. UID 상태와 실시간 리스트로 포지션 흐름을 즉시 확인할 수 있습니다.
            </p>
            <div className="kp-hero__meta">
              <span className="kp-hero__uid">UID {targetUid || uid || "—"}</span>
              {isAdminAccess ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    borderRadius: '999px',
                    background: 'rgba(45, 212, 191, 0.1)',
                    border: '1px solid rgba(94, 234, 212, 0.35)',
                    color: '#99f6e4',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                  }}
                >
                  관리자 연결 모드
                </span>
              ) : null}
              <span>{nickname ? `${nickname}님 환영합니다` : "계정 정보를 불러오는 중"}</span>
            </div>
          </header>

          <div className="kp-console__layout">
            <div className="kp-console__main">
              <article className="kp-card kp-card--controls">
                <div className={`kp-symbol-controls ${mode === "auto" ? "is-mode-auto" : ""}`}>
                  <div className="kp-symbol-dropdown kp-mode-dropdown" ref={modeDropdownRef}>
                    {/* <button
                      type="button"
                      className={`kp-symbol-dropdown__trigger ${isModeMenuOpen ? "is-open" : ""}`}
                      onClick={toggleModeMenu}
                      aria-haspopup="listbox"
                      aria-expanded={isModeMenuOpen}
                    >
                      <span className="kp-symbol-dropdown__current">{currentModeLabel}</span>
                      <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                    </button> */}
                    {isModeMenuOpen && (
                      <div className="kp-symbol-dropdown__menu" role="listbox">
                        {MODE_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`kp-symbol-dropdown__option ${option.value === mode ? "is-active" : ""}`}
                            onClick={() => handleModeSelect(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="kp-symbol-dropdown" ref={symbolDropdownRef}>
                    <button
                      type="button"
                      className={`kp-symbol-dropdown__trigger ${isSymbolMenuOpen ? "is-open" : ""}`}
                      onClick={toggleSymbolMenu}
                      aria-haspopup="listbox"
                      aria-expanded={isSymbolMenuOpen}
                      disabled={mode === "auto"}
                    >
                      <span className="kp-symbol-dropdown__current">{currentSymbolLabel}</span>
                      <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                    </button>
                    {isSymbolMenuOpen && (
                      <div className="kp-symbol-dropdown__menu" role="listbox">
                        <button
                          type="button"
                          className={`kp-symbol-dropdown__option ${symbol === "default" ? "is-active" : ""}`}
                          onClick={() => handleSymbolSelect("default")}
                        >
                          symbol select
                        </button>
                        {selectableSymbols.length > 0 ? (
                          <>
                            <div className="kp-symbol-dropdown__divider" />
                            {selectableSymbols.map((sym) => (
                              <button
                                key={sym}
                                type="button"
                                className={`kp-symbol-dropdown__option ${sym === symbol ? "is-active" : ""}`}
                                onClick={() => handleSymbolSelect(sym)}
                              >
                                {sym}
                              </button>
                            ))}
                          </>
                        ) : (
                          <div className="kp-symbol-dropdown__empty">등록된 심볼이 없습니다</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="kp-symbol-dropdown kp-both-dropdown" ref={bothDropdownRef}>
                    <button
                      type="button"
                      className={`kp-symbol-dropdown__trigger ${isBothMenuOpen ? "is-open" : ""}`}
                      onClick={toggleBothMenu}
                      aria-haspopup="listbox"
                      aria-expanded={isBothMenuOpen}
                    >
                      <span className="kp-symbol-dropdown__current">{currentBothModeLabel}</span>
                      <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                    </button>
                    {isBothMenuOpen && (
                      <div className="kp-symbol-dropdown__menu" role="listbox">
                        {BOTH_MODE_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`kp-symbol-dropdown__option ${option.value === both_mode ? "is-active" : ""}`}
                            onClick={() => handleBothMenuSelect(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="kp-symbol-controls__buttons">
                    <button onClick={handleOpenPopup} className="button_benefit_c_ut" disabled={mode === "auto"}>
                      추 가
                    </button>
                    <button onClick={() => deleteSymbol(symbol)} className="button_benefit_c_ut" disabled={mode === "auto"}>
                      삭 제
                    </button>
                  </div>
                </div>
                <SymbolSearchPopup
                  isOpen={isPopupOpen}
                  onAdd={handleAddSymbol}
                  onClose={handleClosePopup}
                  anchorPosition={popupAnchor}
                />
              </article>
              <AdminSharedPresetPanel
                isAdminAccess={isAdminAccess}
                featureKey="band"
                buildPresetPayload={buildBandPresetPayload}
                applyPreset={applyBandPreset}
                request={fetchBand}
                reloadKey={`${targetUid}:${uid}`}
                singleRow
              />

              <article className="kp-card kp-card--form">
                <div className="contentWrap_sp">

            <div className="line"></div>

            <div className="center_text_lever">Basic setting</div>
              <div class="leverage_container">
                <div class="leverage_item">진입기준</div>
                <select className="select_samaria" value={entry_basis} onChange={handleEntryBasis}>
                  <option value="kinpoint">킨포인트</option>
                  <option value="turtle">터틀</option>
                </select>
                <div class="leverage_item">진입모드</div>
                <select
                  className="select_samaria"
                  value={full_mode}
                  onChange={handleFullMode}
                  disabled={isBandTurtleEntryBasis(entry_basis)}
                  style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}
                >
                  <option value="extension">즉시진입모드</option>
                  <option value="original">변곡진입모드</option>
                </select>
                <div class="leverage_item">{isBandTurtleEntryBasis(entry_basis) ? '정리기준' : '헷지기준'}</div>
                <select className="select_samaria" value={hedge_mode} onChange={handleHedgeMode}>
                  {isBandTurtleEntryBasis(entry_basis) ? (
                    <>
                      <option value="kinpoint">킨포인트</option>
                      <option value="none">미적용</option>
                      <option value="losscut">손절적용</option>
                    </>
                  ) : (
                    <>
                      <option value="kinpoint">극값</option>
                      <option value="grid_exhaust">그리드소진시</option>
                      <option value="none">미적용</option>
                      <option value="losscut">손절적용</option>
                    </>
                  )}
                </select>
              </div>
              <div className="leverage_container">
                <div class="leverage_item">진입·헷지 기준봉</div>
                <select className="select_samaria" value={entry_candle} onChange={handleEntryCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                </select>
                <div
                  class="leverage_item"
                  style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}
                >극값평균 적용</div>
                <select
                  className="select_samaria"
                  value={entry_extreme_avg_mode}
                  onChange={handleEntryExtremeAvgMode}
                  disabled={isBandTurtleEntryBasis(entry_basis)}
                  style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}
                >
                  <option value="disable">미적용</option>
                  <option value="apply">적용</option>
                </select>
                <div
                  class="leverage_item"
                  style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}
                >물타기 적용</div>
                <select
                  className="select_samaria"
                  value={resolveBandDcaModeForHedge(hedge_mode, band_dca_mode)}
                  onChange={handleBandDcaMode}
                  disabled={
                    isBandTurtleEntryBasis(entry_basis)
                    || symbol === 'default'
                    || isBandHedgeGridExhaustMode(hedge_mode)
                  }
                  style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}
                >
                  <option value="disable">미적용</option>
                  <option value="extreme">극값</option>
                  <option value="kinpoint">킨포인트</option>
                </select>
              </div>
              {(band_dca_mode === 'extreme' || band_dca_mode === 'kinpoint') && !isBandTurtleEntryBasis(entry_basis) && (
                <>
                  <div className="center_text_lever" style={{ marginTop: 8 }}>물타기 USDT &amp; 기준간격</div>
                  <div className="wz-entry-table">
                    {bandDcaStepColumns.map((columnRows, columnIdx) => (
                      <div className="wz-entry-table__column" key={`band-dca-col-${columnIdx}`}>
                        {columnRows.map((row) => (
                          <div className="leverage_container_center_kp" key={`band-dca-${row.label}`}>
                            <div className="leverage_item_kp">{row.label}</div>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="leverage_input_kp"
                              placeholder="USDT"
                              title={`${row.label} USDT`}
                              value={row.usdt === 0 ? '' : row.usdt}
                              onChange={(e) => handleBandDcaStepChange(row.originalIdx, 'usdt', e.target.value)}
                              disabled={symbol === 'default'}
                            />
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="leverage_input_kp"
                              placeholder="기준간격(%)"
                              title={`${row.label} 기준간격(%)`}
                              value={row.gap_pct === 0 ? '' : row.gap_pct}
                              onChange={(e) => handleBandDcaStepChange(row.originalIdx, 'gap_pct', e.target.value)}
                              disabled={symbol === 'default'}
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              )}
              <div className="leverage_container">
                <div class="leverage_item">
                  {isBandTurtleEntryBasis(entry_basis) ? '터틀 기준봉 갯수' : '헷지 최고/최저 봉갯수'}
                </div>
                <input
                  type="number"
                  class="leverage_input_basic"
                  min="1"
                  max="999"
                  step="1"
                  value={hedge_hl_bar_count}
                  onChange={handleHedgeHlBarCount}
                  onBlur={() => setHedgeHlBarCount(resolveHedgeHlBarCount())}
                  disabled={symbol === 'default'}
                />
                <div class="leverage_item">
                  {isBandTurtleEntryBasis(entry_basis) ? '롱진입가(최고가)' : '헷지 최고가'}
                </div>
                <input type="text" class="leverage_input_basic" value={hedgeHlHighDisplay} readOnly disabled />
                <div class="leverage_item">
                  {isBandTurtleEntryBasis(entry_basis) ? '숏진입가(최저가)' : '헷지 최저가'}
                </div>
                <input type="text" class="leverage_input_basic" value={hedgeHlLowDisplay} readOnly disabled />
                {!isBandTurtleEntryBasis(entry_basis) && entry_extreme_avg_mode === 'apply' && (
                  <>
                    <div class="leverage_item">극값평균가</div>
                    <input type="text" class="leverage_input_basic" value={hedgeExtremeAvgPx} readOnly disabled />
                  </>
                )}
              </div>
              <p style={{ margin: '0 12px 8px', fontSize: '0.82em', color: '#6a7a8a', lineHeight: 1.45 }}>
                {isBandTurtleEntryBasis(entry_basis)
                  ? '터틀 기준: 최근 N봉 고가·저가가 롱/숏 진입가입니다. 포지션 정리는 정리기준·정리기준신호로 처리합니다.'
                  : '기준봉 N봉 하이킨아시 고·저(극값평균·내부 기준)입니다. 헷지 최고/최저 칸은 HL 헷지 트리거가 걸려 있으면 그 가격을, 없으면 HA 고·저를 표시합니다. 킨포인트 진입 시 헷지(극값)는 HL 돌파로 반대 포지션을 잡고, 헷지 기준 신호로 헷지 다리를 정리합니다.'}
                {(band_dca_mode === 'extreme' || band_dca_mode === 'kinpoint') && !isBandTurtleEntryBasis(entry_basis)
                  ? ' 물타기 1~10단: USDT=추가 진입 금액, 기준간격(%)=직전 기준가 대비 불리하게 벌어진 간격. USDT 0이면 해당 단계부터 중단.'
                  : ''}
                {hedge_mode === 'grid_exhaust' && !isBandTurtleEntryBasis(entry_basis)
                  ? ' 헷지 그리드소진시: 물타기는 킨포인트로 고정됩니다. 저가 돌파마다 물타기 단계 진행 후, 설정 단계가 모두 소진된 뒤에만 헷지 트리거를 걸습니다.'
                  : ''}
              </p>
              <div className="leverage_container">
                <div
                  class="leverage_item"
                  style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}
                >진입 기준 신호</div>
                <select
                  className="select_samaria"
                  value={entry_checking}
                  onChange={handleEntryChecking}
                  disabled={isBandTurtleEntryBasis(entry_basis)}
                  style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}
                >
                  <option value="-1">발생</option>
                  <option value="-2">확정</option>
                </select>
                <div class="leverage_item">
                  {isBandTurtleEntryBasis(entry_basis) ? '정리기준신호' : '헷지 기준 신호'}
                </div>
                <select className="select_samaria" value={hedge_checking} onChange={handleHedgeChecking}>
                  <option value="-1">발생</option>
                  <option value="-2">확정</option>
                </select>
                <div class="leverage_item">최대손절간격(%)</div>
                <input
                  type="number"
                  class="leverage_input_basic"
                  min="0"
                  step="0.1"
                  value={band_max_losscut_pct}
                  onChange={handleBandMaxLosscutPct}
                  disabled={symbol === 'default'}
                  title="0=반대신호만, 0 초과=반대신호 또는 손실% 도달 시 정리(먼저 충족)"
                />
              </div>
              <p style={{ margin: '0 12px 8px', fontSize: '0.82em', color: '#6a7a8a', lineHeight: 1.45 }}>
                {isBandTurtleEntryBasis(entry_basis) ? '정리기준신호' : '헷지 기준 신호'} 반대 신호로 정리하거나,
                최대손절간격(%)이 0보다 크면 손실이 해당 %에 도달해도 정리합니다. 둘 중 먼저 충족되는 조건으로 청산합니다.
                {isBandTurtleEntryBasis(entry_basis)
                  ? ' (터틀: 메인 포지션에 적용)'
                  : ' (킨포인트: 헷지 포지션에 적용)'}
              </p>
              {!isBandTurtleEntryBasis(entry_basis) ? (
                <>
                  <div className="leverage_container">
                    <div class="leverage_item">메인 보전 발동률(%)</div>
                    <input
                      type="number"
                      class="leverage_input_basic"
                      min="0"
                      step="0.1"
                      value={entry_hold_ratio}
                      onChange={handleEntryHoldRatio}
                      disabled={symbol === 'default'}
                    />
                    <div class="leverage_item">메인 보전 모드</div>
                    <select
                      className="select_samaria"
                      disabled={Number(entry_hold_ratio) === 0 || symbol === 'default'}
                      value={entry_hold_mode}
                      onChange={handleEntryHoldMode}
                    >
                      <option value="disable">수수료보전</option>
                      <option value="able">수익보전</option>
                      <option value="hybrid_fwd">하이브리드(정)</option>
                      <option value="hybrid_rev">하이브리드(역)</option>
                    </select>
                    {isBandHybridHoldMode(entry_hold_mode) ? (
                      <>
                        <div class="leverage_item">메인 변환기준(%)</div>
                        <input
                          type="text"
                          inputMode="decimal"
                          class="leverage_input_basic"
                          placeholder="예: 1"
                          title="유리한 이익이 이 %에 도달하면 수수료↔수익 보전 전환"
                          value={formatDecimalPctInputValue(entry_hold_switch_pct)}
                          onChange={handleEntryHoldSwitchPct}
                          onBlur={() => normalizeDecimalPctOnBlur(entry_hold_switch_pct, setEntryHoldSwitchPct)}
                          disabled={symbol === 'default' || Number(entry_hold_ratio) === 0}
                          style={bandFieldBlurStyle(
                            symbol === 'default' || Number(entry_hold_ratio) === 0
                          )}
                        />
                      </>
                    ) : null}
                    <div class="leverage_item">메인 수익보전 ROI(%)</div>
                    <input
                      type="text"
                      inputMode="decimal"
                      class="leverage_input_basic"
                      placeholder="0=기본"
                      title="비우거나 0이면 기존 수익보전. 1~100=최고 수익 대비 N% floor"
                      value={formatDecimalPctInputValue(entry_hold_roi_pct)}
                      onChange={handleEntryHoldRoiPct}
                      onBlur={() => normalizeDecimalPctOnBlur(entry_hold_roi_pct, setEntryHoldRoiPct)}
                      disabled={
                        symbol === 'default'
                        || Number(entry_hold_ratio) === 0
                        || !bandHoldShowsProfitRoi(entry_hold_mode)
                      }
                      style={bandFieldBlurStyle(
                        symbol === 'default'
                        || Number(entry_hold_ratio) === 0
                        || !bandHoldShowsProfitRoi(entry_hold_mode)
                      )}
                    />
                  </div>
                  <p style={{ margin: '0 12px 8px', fontSize: '0.82em', color: '#6a7a8a', lineHeight: 1.45 }}>
                    메인(진입) 포지션이 발동률(%)만큼 수익이 나면 보호청산 트리거를 걸어 메인 포지션을 정리합니다.
                    수수료보전은 진입가±0.15%, 수익보전은 수익 구간을 따라 트리거가 갱신됩니다. (익절 신호와 병행)
                    {entry_hold_mode === 'able' && Number(entry_hold_ratio) > 0 ? (
                      decimalPctToNumber(entry_hold_roi_pct) > 0
                        ? ` 수익보전 ROI ${entry_hold_roi_pct}%: arm 이후 최고 수익 대비 ${entry_hold_roi_pct}% floor를 트레일링합니다.`
                        : ' 수익보전 ROI를 비우면 기존(현재가÷(1±발동률%)) 방식입니다.'
                    ) : ''}
                  </p>
                </>
              ) : null}
              <div className="leverage_container">
                <div class="leverage_item">
                  {isBandTurtleEntryBasis(entry_basis) ? '메인 보전 발동률(%)' : '헷지 보전 발동률(%)'}
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  class="leverage_input_basic"
                  value={hedge_hold_ratio}
                  onChange={handleHedgeHoldRatio}
                  disabled={symbol === 'default'}
                />
                <div class="leverage_item">
                  {isBandTurtleEntryBasis(entry_basis) ? '메인 보전 모드' : '헷지 보전 모드'}
                </div>
                <select
                  className="select_samaria"
                  disabled={Number(hedge_hold_ratio) === 0 || symbol === 'default'}
                  value={hedge_hold_mode}
                  onChange={handleHedgeHoldMode}
                >
                  <option value="disable">수수료보전</option>
                  <option value="able">수익보전</option>
                  <option value="hybrid_fwd">하이브리드(정)</option>
                  <option value="hybrid_rev">하이브리드(역)</option>
                </select>
                {isBandHybridHoldMode(hedge_hold_mode) ? (
                  <>
                    <div class="leverage_item">
                      {isBandTurtleEntryBasis(entry_basis) ? '메인 변환기준(%)' : '헷지 변환기준(%)'}
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      class="leverage_input_basic"
                      placeholder="예: 1"
                      title="유리한 이익이 이 %에 도달하면 수수료↔수익 보전 전환"
                      value={formatDecimalPctInputValue(hedge_hold_switch_pct)}
                      onChange={handleHedgeHoldSwitchPct}
                      onBlur={() => normalizeDecimalPctOnBlur(hedge_hold_switch_pct, setHedgeHoldSwitchPct)}
                      disabled={symbol === 'default' || Number(hedge_hold_ratio) === 0}
                      style={bandFieldBlurStyle(
                        symbol === 'default' || Number(hedge_hold_ratio) === 0
                      )}
                    />
                  </>
                ) : null}
                <div class="leverage_item">
                  {isBandTurtleEntryBasis(entry_basis) ? '메인 수익보전 ROI(%)' : '헷지 수익보전 ROI(%)'}
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  class="leverage_input_basic"
                  placeholder="0=기본"
                  title="비우거나 0이면 기존 수익보전. 1~100=최고 수익 대비 N% floor"
                  value={formatDecimalPctInputValue(hedge_hold_roi_pct)}
                  onChange={handleHedgeHoldRoiPct}
                  onBlur={() => normalizeDecimalPctOnBlur(hedge_hold_roi_pct, setHedgeHoldRoiPct)}
                  disabled={
                    symbol === 'default'
                    || Number(hedge_hold_ratio) === 0
                    || !bandHoldShowsProfitRoi(hedge_hold_mode)
                  }
                  style={bandFieldBlurStyle(
                    symbol === 'default'
                    || Number(hedge_hold_ratio) === 0
                    || !bandHoldShowsProfitRoi(hedge_hold_mode)
                  )}
                />
              </div>
              <p style={{ margin: '0 12px 8px', fontSize: '0.82em', color: '#6a7a8a', lineHeight: 1.45 }}>
                {isBandTurtleEntryBasis(entry_basis)
                  ? '터틀: 메인(진입) 포지션 전량 보전. 메인이 발동률(%)만큼 수익이 나면 보호청산 트리거를 걸습니다. 헷지 다리가 열리면 같은 발동률·모드로 헷지 포지션만 부분 보전합니다.'
                  : '헷지 포지션이 발동률(%)만큼 수익이 나면 보호청산 트리거를 걸어 헷지 포지션만 정리합니다.'}
                {' '}
                수수료보전은 진입가±0.15%, 수익보전은 수익 구간을 따라 트리거가 갱신됩니다.
                {hedge_hold_mode === 'able' && Number(hedge_hold_ratio) > 0 ? (
                  decimalPctToNumber(hedge_hold_roi_pct) > 0
                    ? ` 수익보전 ROI ${hedge_hold_roi_pct}%: arm 이후 최고 수익 대비 ${hedge_hold_roi_pct}% floor를 트레일링합니다.`
                    : ' 수익보전 ROI를 비우면 기존(현재가÷(1±발동률%)) 방식입니다.'
                ) : ''}
              </p>

              <div className="line"></div>

            <div className="center_text_lever">Support setting</div>
              <div className="leverage_container">
                <div class="leverage_item">보조 모드</div>
                <select className="select_samaria" value={entry_standard} onChange={handleEntryStandard}>
                <option value="ema">EMA</option>
                <option value="disable">미적용</option>
                </select>
                <div class="leverage_item">EMA 기준봉갯수</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={ema_standard}
                onChange={handleEmaStandard}
                disabled={entry_standard !== 'ema'}
                style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}/>
                </div>
              <div className="leverage_container">
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}>EMA 손절적용</div>
                <select className="select_samaria" value={ema_losscut}
                disabled={entry_standard !== 'ema'}
                style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}
                onChange={handleEmaLosscut}>
                  <option value="no">미적용</option>
                  <option value="apply">적용</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}>EMA 헷지 이격(%)</div>
                <input
                type="number"
                min="0"
                step="0.01"
                class="leverage_input_basic"
                value={ema_hedge_gap_ratio}
                onChange={handleEmaHedgeGapRatio}
                disabled={entry_standard !== 'ema'}
                style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}/>
              </div>
              <div className="leverage_container">
                <div class="leverage_item" style={{
                  filter: (entry_standard === 'disable') ? 'blur(5px)' : 'none',
                }}>보조 시간봉</div>
                <select className="select_samaria" value={candle_standard}
                disabled={entry_standard === 'disable'}
                style={{
                  filter: (entry_standard === 'disable') ? 'blur(5px)' : 'none',
                }}
                onChange={handleCandleStandard}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (entry_standard === 'disable') ? 'blur(5px)' : 'none',
                }} >신호 기준</div>
                <select className="select_samaria" value={candle_type_standard}
                disabled={entry_standard === 'disable'}
                style={{
                  filter: (entry_standard === 'disable') ? 'blur(5px)' : 'none',
                }}
                onChange={handleCandleTypeStandard}>
                  <option value="haikin">하이킨아시</option>
                  <option value="normal">일반봉</option>
                </select>
              </div>

              <div className="line"></div>
              <div
                className="center_text_lever"
                style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}
              >Close benefit setting</div>

              <div style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}>
              <div class="leverage_container">
              <div class="leverage_item">단일 익절 기준</div>
                <select
                  className="select_samaria"
                  value={close_mode}
                  onChange={handleCloseMode}
                  disabled={isBandTurtleEntryBasis(entry_basis)}
                >
                  <option value="kinpoint">킨포인트</option>
                  <option value="extreme_hl">극값익절</option>
                </select>
                <div class="leverage_item">익절 기준봉</div>
                  <select className="select_samaria" value={close_candle}
                  onChange={handleCloseCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                  </select>
                </div>
              <div class="leverage_container">
                <div class="leverage_item" style={{
                  filter: close_mode === 'extreme_hl' ? 'blur(5px)' : 'none',
                }}>익절 기준 신호</div>
                  <select className="select_samaria"
                    value={close_checking}
                    onChange={handleCloseChecking}
                    disabled={close_mode === 'extreme_hl'}
                    style={{
                      filter: close_mode === 'extreme_hl' ? 'blur(5px)' : 'none',
                    }}>
                    <option value="-1">발생</option>
                    <option value="-2">확정</option>
                  </select>
                  <div class="leverage_item" style={{
                    filter: close_mode !== 'extreme_hl' ? 'blur(5px)' : 'none',
                  }}>극값기준봉 갯수</div>
                <input
                  type="number"
                  class="leverage_input_basic"
                  min="1"
                  max="999"
                  step="1"
                  value={close_tp_hl_bar_count}
                  onChange={handleCloseTpHlBarCount}
                  onBlur={() => setCloseTpHlBarCount(resolveCloseTpHlBarCount())}
                  disabled={close_mode !== 'extreme_hl' || symbol === 'default'}
                  style={{
                    filter: close_mode !== 'extreme_hl' ? 'blur(5px)' : 'none',
                  }}
                />
                  <div class="leverage_item">익절 후 연속 진입</div>
                  <select className="select_samaria" value={single_tp_continue_mode} onChange={handleSingleTpContinueMode}>
                    <option value="disable">미적용</option>
                    <option value="able">적용</option>
                  </select>
              </div>
              {close_mode === 'extreme_hl' && (
                <div className="leverage_container">
                  <div class="leverage_item">익절 최고가</div>
                  <input type="text" class="leverage_input_basic" value={closeHlHighPx} readOnly disabled />
                  <div class="leverage_item">익절 최저가</div>
                  <input type="text" class="leverage_input_basic" value={closeHlLowPx} readOnly disabled />
                </div>
              )}
              {close_mode === 'extreme_hl' && (
                <p style={{ margin: '0 12px 8px', fontSize: '0.82em', color: '#6a7a8a', lineHeight: 1.45 }}>
                  익절 기준봉 최근 N봉 하이킨아시 고저가입니다. 롱은 최저가·숏은 최고가 도달 시 최소익절률 이상 수익이면 익절합니다.
                </p>
              )}
              </div>

              <div className="line"></div>

              <div className="leverage_container">
                <div class="leverage_item">Set Leverage</div>
                  <input
                  type="number"
                  class="leverage_input_basic"
                  step="1"
                  value={leverage_info}
                  onChange={handleLeverage} />
                <div class="leverage_item" style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}>최소 익절률(%)</div>
                  <input
                  type="number"
                  class="leverage_input_basic"
                  value={benefit_ratio}
                  onChange={handleBenefitratio}
                  disabled={isBandTurtleEntryBasis(entry_basis)}
                  style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))} />
                <div class="leverage_item" disabled={both_mode === 'double'}
                    style={{
                      filter: (both_mode === 'double') ? 'blur(5px)' : 'none',
                    }}>진입 방향 선택</div>
                  <select className="select_samaria" value={direction} onChange={handleDirection}
                  disabled={both_mode === 'double'}
                    style={{
                      filter: (both_mode === 'double') ? 'blur(5px)' : 'none',
                    }}>
                    <option value='long'>롱</option>
                    <option value='short'>숏</option>
                    <option value='auto'>자동전환</option>
                  </select>
              </div>

            <div class="leverage_container">
              <div class="leverage_item" disabled={both_mode !== 'double'}
                  style={{
                    filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                  }}>복리 모드 선택</div>
                <select className="select_samaria" value={welfare_mode} onChange={handleWelfareMode}
                    disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>
                    <option value='disable'>미적용</option>
                    <option value='able'>적용</option>
                  </select>
                <div class="leverage_item" disabled={both_mode !== 'double' || welfare_mode === 'disable'}
                    style={{
                      filter: (both_mode !== 'double' || welfare_mode === 'disable') ? 'blur(5px)' : 'none',
                    }}>진입 비율(%)</div>
                  <input
                  type="number"
                  class="leverage_input_basic"
                  disabled={both_mode !== 'double' || welfare_mode === 'disable'}
                  style={{
                    filter: (both_mode !== 'double' || welfare_mode === 'disable') ? 'blur(5px)' : 'none',
                  }}
                  value={welfare_ratio}
                  onChange={handleWelfareRatio} />
              <div className="leverage_item">진입 USDT</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                disabled={welfare_mode === 'able' && both_mode === 'double'}
                value={entry_usdt_1}
                onChange={handleEntryUsdt_1} />
              </div>

              <div class="leverage_container">
                <div class="leverage_item">RealizedPnL</div>
                    <input
                    type="number"
                    class="leverage_input_basic"
                    disabled={true}
                    value={totalpnl} />
                <div class="leverage_item">헷지 연결 포지션</div>
                  <select className="select_samaria" value={hedge_continue} onChange={handleHedgeContinue}
                      disabled={mode === 'auto'}
                      style={{
                        filter: (mode === 'auto') ? 'blur(5px)' : 'none',
                        }}>
                    <option value='no'>미적용</option>
                    <option value='ok'>적용</option>
                  </select>
              </div>

              <div className="line"></div>
              <div
                className="center_text_lever"
                style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}
              >
                {both_mode === 'double' ? '심볼별 더블익절 셋팅' : '심볼별 누적익절 셋팅'}
              </div>
            <div style={bandFieldBlurStyle(isBandTurtleEntryBasis(entry_basis))}>
            <div className="leverage_container">
                <div class="leverage_item">
                  {both_mode === 'double' ? '더블익절설정(%)' : '누적익절설정(%)'}
                </div>
                  <input
                  type="number"
                  class="leverage_input_basic"
                  value={double_benefit}
                  onChange={handleDoubleBenefit}
                  disabled={isBandTurtleEntryBasis(entry_basis)}
                />
                <div class="leverage_item">현재수익(USDT)</div>
                  <input
                  type="number"
                  class="leverage_input_basic"
                  value={double_pnl}
                  disabled = {true} />
                <div class="leverage_item">
                  {both_mode === 'double' ? '더블연속모드' : '누적익절 연속'}
                </div>
                  <select
                    className="select_samaria"
                    value={double_continue}
                    onChange={handleDoubleContinue}
                    disabled={isBandTurtleEntryBasis(entry_basis)}
                  >
                    <option value="disable">미적용</option>
                    <option value="able">적용</option>
                  </select>
              </div>
            </div>

            <div className="line"></div>
            <div className="center_text_lever">잔고 조회 및 종합익절 셋팅</div>
              <div class="leverage_container">
                <div class="leverage_item">메인 잔고 조회(USDT)</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={main_balance}
                disabled = {true} />
                <div class="leverage_item"  disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>2nd 잔고 조회(USDT)</div>
                <input
                type="number"
                class="leverage_input_basic"
                style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}
                value={second_balance}
                disabled = {true} />
                <div class="leverage_item">총합 잔고(USDT)</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={Number(main_balance + second_balance).toFixed(3)}
                disabled = {true} />
              </div>
               <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickMainBalance(symbol)} className="button_benefit_c_lever">
                메인 잔고 조회
                </button>
                <button onClick={() => onClickSecondBalance(symbol)} className="button_benefit_c_lever"
                  disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>
                2nd 잔고 조회
                </button>
                <button onClick={() => onClickDoublePnl(symbol)} className="button_benefit_c_lever"
                  disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>
                더블수익 조회
                </button>
              </div>
              <div class="leverage_container">
                <div class="leverage_item">종합 익절 셋팅(%)</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={total_benefit_usdt}
                onChange={handleTotalBenefitusdt} />
                <div class="leverage_item">종합 익절 목표값(USDT)</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={Number(total_benefit_usdt_obj).toFixed(3)}
                disabled = {true} />
              </div>
              <div class="leverage_container">
                <div class="leverage_item">종합 익절 목표 횟수</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={total_benefit_count}
                onChange={handleTotalBenefitCount} />
                <div class="leverage_item">종합 익절 현재 횟수</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={total_current_count}
                disabled = {true} />
              </div>
              <div class="leverage_container">
                <div class="leverage_item">종합 익절 연속 모드</div>
                <select className="select_samaria" value={total_continue_mode} onChange={handleTotalContinueMode}>
                  <option value="disable">미적용</option>
                  <option value="able">적용</option>
                </select>
                <div class="leverage_item">연속 모드 시 진행 방향</div>
                <select className="select_samaria" value={total_continue_direction} onChange={handleTotalContinueDirection}>
                  <option value="both">양방향</option>
                  <option value="long">롱</option>
                  <option value="short">숏</option>
                </select>
              </div>
               <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickTotal_search(symbol)} className="button_benefit_c_lever">
                종합 익절 목표값 확인
                </button>
                <button onClick={() => onClickTotal_start(symbol)} className="button_benefit_c_lever">
                종합 익절 시작
                </button>
                <button onClick={() => onClickTotal_cancel(symbol)} className="button_benefit_c_lever">
                종합 익절 취소
                </button>
              </div>
              <div className="leverage_container">
                <div
                  className={`leverage_item total-benefit-status ${isTotalBenefitRunning ? 'is-running' : 'is-stopped'}`}
                >
                  {isTotalBenefitRunning ? '종합익절 구동중' : '종합익절 정지중'}
                </div>
              </div>
                </div>{/* contentWrap_sp 닫힘 */}
              <div className="line"></div>
              <OpenPositionsSnapshotSection
                endpoint="/band_open_positions_snapshot"
                enabled={Boolean(uid)}
                reloadKey={`${uid}-${targetUid}-${isAdminAccess ? "1" : "0"}-${snapshotReloadToken}`}
                fetchImpl={fetchBand}
                title="메인/보조 오픈 포지션"
                updatedAtPrefix="마지막 갱신:"
                loadingRefreshLabel="조회중..."
                onSelectSymbol={onClickCoin}
                selectedSymbol={symbol}
                showAdminManualTrade={showAdminManualTrade}
                onAdminNewEntry={
                  showAdminManualTrade
                    ? (e) => setAdminManualModal({
                        mode: "new",
                        anchorRect: snapshotAnchorRect(e.currentTarget),
                      })
                    : undefined
                }
                onAdminAdjustLeg={
                  showAdminManualTrade
                    ? (payload) => setAdminManualModal({
                        mode: "leg",
                        account: payload.account,
                        item: payload.item,
                        anchorRect: payload.anchorRect ?? null,
                      })
                    : undefined
                }
                onHasSecondAccountChange={setSnapshotHasSecondAccount}
              />
              {showAdminManualTrade && adminManualModal ? (
                <HoldAdminManualModal
                  open
                  payload={adminManualModal}
                  onClose={() => setAdminManualModal(null)}
                  onOrderSuccess={() => setSnapshotReloadToken((t) => t + 1)}
                  fetchHold={fetchBand}
                  manualOrderEndpoint="/band/admin_manual_order"
                  hasSecondAccount={both_mode === "double" || snapshotHasSecondAccount}
                  pageSymbol={symbol}
                  selectableSymbols={selectableSymbols}
                  holdTargetUid={targetUid}
                  isAdminAccess={isAdminAccess}
                  pageLeverage={leverage_info}
                />
              ) : null}
              <div className="line"></div>
              
            {/* <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickAutoStart()} disabled={mode === 'normal'}
                style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                  }} className="button_benefit_c_lever">
                자동 매매 시작
                </button>
                <button onClick={() => onClickStop()} className="button_benefit_c_lever">
                자동 매매 종료
                </button>
            </div>   */}
                {/* ── 진입 제한 설정 버튼 (관리자 + 심볼 선택 시) ── */}
            {isAdminAccess && symbol && symbol !== 'default' && (
              <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button
                  onClick={openBandRestrictModal}
                  className="bottomButton_sp_lever"
                  style={{ background: 'rgba(231,76,60,0.15)', border: '1px solid rgba(231,76,60,0.5)', color: '#e74c3c', transition: 'background 0.18s, border-color 0.18s, box-shadow 0.18s' }}
                  onMouseEnter={e => { e.currentTarget.style.background='rgba(231,76,60,0.35)'; e.currentTarget.style.borderColor='rgba(231,76,60,0.9)'; e.currentTarget.style.boxShadow='0 0 8px rgba(231,76,60,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='rgba(231,76,60,0.15)'; e.currentTarget.style.borderColor='rgba(231,76,60,0.5)'; e.currentTarget.style.boxShadow='none'; }}
                >
                  진입 제한 시간 설정
                </button>
              </div>
            )}

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickStart()} disabled={mode === 'auto'}
                style={{
                    filter: (mode === 'auto') ? 'blur(5px)' : 'none',
                  }} className="bottomButton_sp_lever">
                일반 매매 시작
                </button>
            </div>

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickBenefit_stop(symbol)} disabled={benefit_stop === 1 || trade_start === 0 || benefit_stop === '1' || trade_start === '0'} className="button_benefit_c_lever">
                익절 후 정리
                </button>
                <button onClick={() => onClickBenefit_cancel(symbol)} disabled={benefit_stop === '0' || benefit_stop === 0} className="button_benefit_c_lever">
                취 소
                </button>
            </div>

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickCurrentCoin_stop(symbol)} disabled={symbol === 'default'} className="bottomButton_sp_lever">
                현재 코인 정리
                </button>
            </div>

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickEnd()} disabled={notAllow} className="bottomButton_sp_lever">
                로그아웃
                </button>
             </div>
              </article>
            </div>

            <aside className="kp-console__side">
              <article className="kp-card kp-card--list">
                <div className="list_box_title_lever">진입중인 코인 리스트</div>
                <ul className="list_box_lever">
                  {e_items.map((item, index) => (
                    <li key={index} className="list_item" onClick={() => onClickCoin(item)}>
                      {item}
                    </li>
                  ))}
                </ul>
              </article>

              <article className="kp-card kp-card--list">
                <div className="list_box_title_lever">익절 후 정리 리스트</div>
                <ul className="list_box_lever">
                  {w_items.map((item, index) => (
                    <li key={index} className="list_item" onClick={() => onClickCoin(item)}>
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </aside>
          </div>
        </section>

      {bandRestrictModalOpen && bandRestrictDraft && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={closeBandRestrictModal}
          role="presentation"
        >
          <div
            style={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '12px', padding: '28px', width: '360px', maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: '20px' }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#aaa', letterSpacing: '0.05em' }}>Hold / {symbol}</p>
                <h3 style={{ margin: '4px 0 0', fontSize: '1rem', color: '#fff' }}>진입 제한 시간대 설정</h3>
              </div>
              <button type="button" onClick={closeBandRestrictModal} style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1, transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color='#fff'}
                  onMouseLeave={e => e.currentTarget.style.color='#aaa'}>×</button>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={bandRestrictDraft.restrict_enabled}
                onChange={handleHoldRestrictDraftChange('restrict_enabled')}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 600, color: '#fff' }}>제한 시간대 활성화</span>
              {bandRestrictDraft.restrict_enabled
                ? <span style={{ color: '#e74c3c', fontWeight: 700, fontSize: '0.85rem' }}>ON</span>
                : <span style={{ color: '#888', fontSize: '0.85rem' }}>OFF</span>}
            </label>

            {bandRestrictDraft.restrict_enabled && (
              <>
                {/* ── 프리셋 불러오기 ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontSize: '0.82rem', color: '#aaa' }}>프리셋</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <select
                      value={bandRestrictSelectedPresetId}
                      onChange={(e) => setHoldRestrictSelectedPresetId(e.target.value)}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: bandRestrictSelectedPresetId ? '#fff' : '#777' }}
                    >
                      <option value="">-- 프리셋 선택 --</option>
                      {bandRestrictPresets.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        if (!bandRestrictSelectedPresetId) return;
                        const p = bandRestrictPresets.find((x) => String(x.id) === String(bandRestrictSelectedPresetId));
                        if (!p) return;
                        setHoldRestrictDraft((prev) => ({
                          ...prev,
                          restrict_start_day:  p.start_day,
                          restrict_start_time: p.start_time,
                          restrict_end_day:    p.end_day,
                          restrict_end_time:   p.end_time,
                        }));
                        setHoldRestrictPresetMsg('');
                      }}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #555', background: 'rgba(255,255,255,0.08)', color: '#ccc', cursor: bandRestrictSelectedPresetId ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', transition: 'background 0.15s, border-color 0.15s', opacity: bandRestrictSelectedPresetId ? 1 : 0.4 }}
                      onMouseEnter={e => { if (!bandRestrictSelectedPresetId) return; e.currentTarget.style.background='rgba(255,255,255,0.18)'; e.currentTarget.style.borderColor='#888'; }}
                      onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor='#555'; }}
                    >
                      불러오기
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!bandRestrictSelectedPresetId) return;
                        if (!window.confirm('선택한 프리셋을 삭제할까요?')) return;
                        const res = await fetchBand(`/admin/band_restrict_presets/${bandRestrictSelectedPresetId}`, { method: 'DELETE' });
                        if (res.ok) {
                          setHoldRestrictPresets((prev) => prev.filter((x) => String(x.id) !== String(bandRestrictSelectedPresetId)));
                          setHoldRestrictSelectedPresetId('');
                          setHoldRestrictPresetMsg('🗑 삭제됐습니다');
                        }
                      }}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid rgba(231,76,60,0.4)', background: 'rgba(231,76,60,0.08)', color: '#e74c3c', cursor: bandRestrictSelectedPresetId ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', transition: 'background 0.15s, border-color 0.15s', opacity: bandRestrictSelectedPresetId ? 1 : 0.4 }}
                      onMouseEnter={e => { if (!bandRestrictSelectedPresetId) return; e.currentTarget.style.background='rgba(231,76,60,0.22)'; e.currentTarget.style.borderColor='rgba(231,76,60,0.8)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background='rgba(231,76,60,0.08)'; e.currentTarget.style.borderColor='rgba(231,76,60,0.4)'; }}
                    >
                      삭제
                    </button>
                  </div>
                  {/* ── 현재 설정 저장 ── */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="text"
                      placeholder="프리셋 이름 입력"
                      value={bandRestrictPresetName}
                      onChange={(e) => setHoldRestrictPresetName(e.target.value)}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: '#fff' }}
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if (!bandRestrictPresetName.trim()) return;
                        const res = await fetchBand('/admin/band_restrict_presets', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            name:       bandRestrictPresetName.trim(),
                            start_day:  bandRestrictDraft.restrict_start_day,
                            start_time: bandRestrictDraft.restrict_start_time,
                            end_day:    bandRestrictDraft.restrict_end_day,
                            end_time:   bandRestrictDraft.restrict_end_time,
                          }),
                        });
                        const data = await res.json();
                        if (res.ok && data.ok) {
                          setHoldRestrictPresets((prev) => [...prev, {
                            id: data.id, name: bandRestrictPresetName.trim(),
                            start_day:  bandRestrictDraft.restrict_start_day,
                            start_time: bandRestrictDraft.restrict_start_time,
                            end_day:    bandRestrictDraft.restrict_end_day,
                            end_time:   bandRestrictDraft.restrict_end_time,
                          }]);
                          setHoldRestrictPresetName('');
                          setHoldRestrictPresetMsg('✅ 저장됐습니다');
                        } else {
                          setHoldRestrictPresetMsg(`❌ ${data.error || '저장 실패'}`);
                        }
                      }}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #555', background: 'rgba(255,255,255,0.08)', color: '#ccc', cursor: bandRestrictPresetName.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', transition: 'background 0.15s, border-color 0.15s', opacity: bandRestrictPresetName.trim() ? 1 : 0.4 }}
                      onMouseEnter={e => { if (!bandRestrictPresetName.trim()) return; e.currentTarget.style.background='rgba(255,255,255,0.18)'; e.currentTarget.style.borderColor='#888'; }}
                      onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor='#555'; }}
                    >
                      저장
                    </button>
                  </div>
                  {bandRestrictPresetMsg && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: bandRestrictPresetMsg.startsWith('✅') ? '#2ecc71' : bandRestrictPresetMsg.startsWith('🗑') ? '#aaa' : '#e74c3c' }}>
                      {bandRestrictPresetMsg}
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '0.82rem', color: '#aaa' }}>시작</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select value={bandRestrictDraft.restrict_start_day} onChange={handleHoldRestrictDraftChange('restrict_start_day')}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: '#fff' }}>
                      {HOLD_DAY_LABELS.map((label, idx) => <option key={idx} value={idx}>{label}요일</option>)}
                    </select>
                    <input type="time" value={bandRestrictDraft.restrict_start_time} onChange={handleHoldRestrictDraftChange('restrict_start_time')}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: '#fff' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '0.82rem', color: '#aaa' }}>종료</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select value={bandRestrictDraft.restrict_end_day} onChange={handleHoldRestrictDraftChange('restrict_end_day')}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: '#fff' }}>
                      {HOLD_DAY_LABELS.map((label, idx) => <option key={idx} value={idx}>{label}요일</option>)}
                    </select>
                    <input type="time" value={bandRestrictDraft.restrict_end_time} onChange={handleHoldRestrictDraftChange('restrict_end_time')}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: '#fff' }} />
                  </div>
                </div>
                <p style={{ fontSize: '0.82rem', color: '#aaa', margin: 0, lineHeight: 1.6 }}>
                  📅 <strong style={{ color: '#fff' }}>
                    {HOLD_DAY_LABELS[bandRestrictDraft.restrict_start_day]}요일 {bandRestrictDraft.restrict_start_time}
                    &nbsp;~&nbsp;
                    {HOLD_DAY_LABELS[bandRestrictDraft.restrict_end_day]}요일 {bandRestrictDraft.restrict_end_time}
                  </strong>
                  <br />이 시간대에 신규 진입 차단 (기존 포지션은 유지)
                </p>
              </>
            )}

            {bandRestrictSaveMsg && (
              <p style={{ margin: 0, fontSize: '0.9rem', color: bandRestrictSaveMsg.startsWith('✅') ? '#2ecc71' : '#e74c3c' }}>{bandRestrictSaveMsg}</p>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={closeBandRestrictModal} disabled={bandRestrictSaveLoading}
                style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #555', background: 'transparent', color: '#ccc', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor='#888'; }}
                onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor='#555'; }}>
                취소
              </button>
              <button type="button" onClick={handleHoldRestrictSave} disabled={bandRestrictSaveLoading}
                style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', background: '#e74c3c', color: '#fff', fontWeight: 700, cursor: 'pointer', transition: 'background 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background='#c0392b'; e.currentTarget.style.boxShadow='0 0 8px rgba(231,76,60,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.background='#e74c3c'; e.currentTarget.style.boxShadow='none'; }}>
                {bandRestrictSaveLoading ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
      </main>
    );
}
 